import { useState, useRef, useEffect, useMemo } from "react";
import * as api from "../api";
import Icon from "../components/Icon";
import { BtnPrimary } from "../components/Atoms";
import { Badge } from "../components/Atoms";
import { useConfirmar } from "../components/Confirmar";
import {
  AG_PRIOS, AG_ESTADOS, agOrigen,
  PR_CLR, PR_BG, AG_EST_CLR, AG_PROD_BANDS, ST_CLR
} from "../constants";
import { uid, fmt, fmtFull, today, getTipoColor, getTipoIcon, tiposUsageMap, colorConBlanco,
         shortName, parseTimeInput, fmtHours, esNoLaborable,
         getTipoProd, prodBand, prodStats, fmtRatio,
         puedeVerCuadernoAjeno, cuadernosVisiblesPor, equiposDe, esAdmin, esSuper } from "../utils";
import DatePicker from "../components/forms/DatePicker";
import TypePill from "../components/widgets/TypePill";
import TipoEntradaModal from "../components/modals/TipoEntradaModal";
import SelectorAsignacion from "../components/widgets/SelectorAsignacion";
import FiltroFechas from "../components/widgets/FiltroFechas";
import { usePosicionDesplegable } from "../components/posicionDesplegable";
import CronologiaDiaView from "../components/timeline/CronologiaDiaView";
import SubtareasEditor from "../components/forms/SubtareasEditor";
import { normaliza as normalizaSubtareas, nuevoId as nuevoIdSubtarea,
         toggle as toggleSubtarea, resumenSubtareas } from "../subtareas";
import Campo from "../components/forms/Campo";
import OrigenIcono from "../components/misTareas/OrigenIcono";
import usePoolMisTareas from "../hooks/usePoolMisTareas";
import { rellenarDestinoDeIncidencia } from "../destinoIncidencia";
import { clicable } from "../components/ClicableTeclado.jsx";

// Fuera del className: el comprobador de clases de pruebas/clases-css.js lee
// los literales que aparecen dentro de un className, y "activo" ahi parecia
// una clase CSS que nadie habia declarado.
const ESTADO_ACTIVO = "activo";

// Iconos de la barra movil: trazo 1,7 y viewBox 24, distintos del set de relleno de Icon.
const TB_ICONOS = {
  filtros: <path d="M3 5h18l-7 8.5V20l-4-2v-4.5z"/>,
  importar: <><path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4"/><path d="M12 15V4"/><path d="M7.5 8.5 12 4l4.5 4.5"/></>,
  pdf: <><path d="M6 2.5h8.5L19.5 7.5V21a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5z"/><path d="M14.5 2.5v5h5"/><text x="12.5" y="17.6" textAnchor="middle" fontFamily="DM Sans,Arial,sans-serif" fontSize="7.6" fontWeight="800" fill="currentColor" stroke="none">PDF</text></>,
  tabla: <><path d="M8 6h12M8 12h12M8 18h12"/><circle cx="4" cy="6" r=".6"/><circle cx="4" cy="12" r=".6"/><circle cx="4" cy="18" r=".6"/></>,
  crono: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  dash: <path d="M5 20V11M12 20V4M19 20v-6"/>,
  otra: <><circle cx="9" cy="8" r="3.2"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M16 5.2a3 3 0 0 1 0 5.6M18.5 14.5c1.6.8 2.5 2.7 2.5 5.5"/></>,
  tipos: <><path d="M3 12V4.5A1.5 1.5 0 0 1 4.5 3H12l9 9-8 8z"/><circle cx="8" cy="8" r="1.2"/></>,
  chev: <path d="M6 9l6 6 6-6"/>,
};
function TbIcono({ ico, size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{flexShrink:0}}>
      {TB_ICONOS[ico]}
    </svg>
  );
}
const VISTAS_TB = [["tabla","Registro"],["crono","Cronología"],["dash","Resumen"]];

function NotionTag({ item, parentItem }) {
  const [show, setShow] = useState(false);
  if (!item) return null;
  return (
    <span
      className="cw-mob-hide cw-chip"
      style={{ position:'relative', display:'inline-flex', alignItems:'center', gap:3, flexShrink:0,
        fontSize:11, fontWeight:700, padding:'2px 6px', borderRadius:20,
        background:'var(--info-bg)', color:'var(--info-text)', whiteSpace:'nowrap', cursor:'default' }}
      onMouseEnter={()=>setShow(true)}
      onMouseLeave={()=>setShow(false)}
    >
      {item.icon&&<span style={{fontSize:11}}>{item.icon}</span>}
      <span style={{maxWidth:140, overflow:'hidden', textOverflow:'ellipsis'}}>{item.title}</span>
      {show && parentItem && (
        <span style={{
          position:'absolute', bottom:'calc(100% + 7px)', left:'50%', transform:'translateX(-50%)',
          background:'#1e293b', color:'#fff', fontSize:11, fontWeight:500,
          padding:'5px 10px', borderRadius:7, whiteSpace:'nowrap', zIndex:9999,
          boxShadow:'0 2px 10px rgba(0,0,0,.28)', pointerEvents:'none', lineHeight:1.4,
        }}>
          {parentItem.icon&&<span style={{marginRight:4}}>{parentItem.icon}</span>}
          {parentItem.title}
          <span style={{
            position:'absolute', top:'100%', left:'50%', transform:'translateX(-50%)',
            border:'5px solid transparent', borderTopColor:'#1e293b',
            display:'block', width:0, height:0,
          }}/>
        </span>
      )}
    </span>
  );
}

function HoursInput({value, onChange, id, "aria-labelledby":etiquetadoPor}) {
  const [raw, setRaw] = useState(null);
  const handleFocus = () => {
    const v = parseFloat(value)||0;
    const mins = Math.round(v*60);
    setRaw(mins < 60 ? `${mins}m` : `${parseFloat(v.toFixed(2))}h`);
  };
  const commit = () => {
    if(raw !== null) { onChange(parseTimeInput(raw)); setRaw(null); }
  };
  return (
    <input type="text" id={id} aria-labelledby={etiquetadoPor}
      value={raw !== null ? raw : fmtHours(value)}
      onChange={e=>setRaw(e.target.value)}
      onFocus={handleFocus}
      onBlur={commit}
      onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
      placeholder="ej: 1.5h, 30m"
      style={{textAlign:"right"}}/>
  );
}

// Hora en 24 h (HH:mm), sin AM/PM: es lo normal en España y el <input type="time">
// pinta AM/PM segun el idioma del navegador y en un campo estrecho recorta el
// sufijo. Se escribe como texto: "930", "9:30" o "09:30" valen y se normalizan al
// salir. El valor que sube sigue siendo la cadena "HH:mm" de siempre (o "").
function HoraInput({value, onChange, id, "aria-labelledby":etiquetadoPor, "aria-label":etiqueta}) {
  const [raw, setRaw] = useState(null);
  const normaliza = t => {
    const s = String(t||"").trim().replace(/[.,h]/g,":");
    if(!s) return "";
    let h, m;
    if(s.includes(":")) { [h, m = "0"] = s.split(":"); }
    else if(/^\d{3,4}$/.test(s)) { h = s.slice(0, s.length-2); m = s.slice(-2); }
    else { h = s; m = "0"; }
    if(!/^\d{1,2}$/.test(h) || !/^\d{1,2}$/.test(m)) return null;
    if(+h > 23 || +m > 59) return null;
    return String(+h).padStart(2,"0")+":"+String(+m).padStart(2,"0");
  };
  const commit = () => {
    if(raw === null) return;
    const n = normaliza(raw);
    if(n !== null && n !== (value||"")) onChange(n);
    setRaw(null);
  };
  return (
    <input type="text" id={id} aria-labelledby={etiquetadoPor} aria-label={etiquetadoPor?undefined:etiqueta}
      inputMode="numeric" autoComplete="off" maxLength={5} placeholder="hh:mm"
      value={raw !== null ? raw : (value||"")}
      onChange={e=>setRaw(e.target.value.replace(/[^\d:.,h]/g,""))}
      onFocus={e=>{setRaw(value||"");e.target.select();}}
      onBlur={commit}
      onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
      style={{textAlign:"center",fontVariantNumeric:"tabular-nums"}}/>
  );
}

function RecipientInput({value, onChange, onSelectUser, allUsers=[], id, "aria-labelledby":etiquetadoPor}) {
  const [q,    setQ   ] = useState(value||"");
  const [open, setOpen] = useState(false);
  const [activo, setActivo] = useState(0);   // sugerencia resaltada con el teclado
  const ref  = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(open, ref, panelRef, { altoMax: 200, separacion: 2, igualarAncho: true });
  const filas = useRef([]);

  useEffect(()=>{ setQ(value||""); }, [value]);
  useEffect(()=>{
    function h(e){ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[]);

  const suggestions = allUsers.filter(u=>u.active!==false&&u.name.toLowerCase().includes(q.toLowerCase())).slice(0,8);

  // Al cambiar lo escrito, el resaltado vuelve arriba: si no, podia quedarse
  // apuntando a una posicion que ya no existe en la lista nueva.
  useEffect(()=>{ setActivo(0); }, [q]);

  // Con lista larga, moverse con el teclado tiene que arrastrar el scroll.
  useEffect(()=>{
    if(open) filas.current[activo]?.scrollIntoView({block:"nearest"});
  }, [activo, open]);

  const teclas = (e) => {
    if(!open || !suggestions.length){
      if(e.key==="ArrowDown") setOpen(true);
      return;
    }
    if(e.key==="ArrowDown"){ e.preventDefault(); setActivo(i=>(i+1)%suggestions.length); }
    else if(e.key==="ArrowUp"){ e.preventDefault(); setActivo(i=>(i-1+suggestions.length)%suggestions.length); }
    else if(e.key==="Enter"){
      // Solo se traga el Enter si hay algo que elegir: si no, seguiria siendo
      // el Enter normal del formulario.
      e.preventDefault();
      select(suggestions[activo]);
    }
    else if(e.key==="Escape"){ e.stopPropagation(); setOpen(false); }
    else if(e.key==="Tab"){ setOpen(false); }
  };

  const select = (u) => {
    setQ(u.name);
    if(onSelectUser) onSelectUser(u); else onChange(u.name);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{position:"relative"}}>
      <input id={id} aria-labelledby={etiquetadoPor} value={q}
        onChange={e=>{ setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={()=>setOpen(true)}
        onBlur={()=>setTimeout(()=>setOpen(false),150)}
        onKeyDown={teclas}
        placeholder="Nombre del destinatario"/>
      {open&&suggestions.length>0&&(
        <div ref={panelRef} style={{zIndex:300,
          background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,
          boxShadow:"var(--shadow-lg)",...posicion}}>
          {suggestions.map((u,i)=>(
            <div key={u.id} onMouseDown={()=>select(u)}
              ref={el=>{ filas.current[i]=el; }}
              style={{padding:"6px 10px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:8,
                background: i===activo ? "var(--accent-bg)" : "transparent"}}
              // El raton manda sobre el teclado: mover el cursor traslada el
              // resaltado, en vez de dejar dos filas marcadas a la vez.
              onMouseEnter={()=>setActivo(i)}>
              <span style={{width:22,height:22,borderRadius:"50%",background:"var(--accent)",color:"var(--on-accent)",
                fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {u.name.charAt(0).toUpperCase()}
              </span>
              <span style={{fontWeight:600,color:"var(--text)"}}>{u.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Fila de entrada del cuaderno: vista compacta + formulario expandido.
// Se comparte entre los grupos por tipo y el grupo "Otros" — ese grupo no tenía
// formulario, así que una entrada sin tipo no había forma de arreglarla desde la UI.
export function EntryRow({
  a, col, isEdit, draggable=false, dragging=false, festivos={},
  activeTipos=[], archivedTipos=[], teams=[], allUsers=[], notionItems=[], misPasos=[],
  editTimes, setEditTimes, cronoEstado, onAbrirIncidencia, canVerIncidencias=false,
  onOpen, onCloseEdit, onDragStartEntry, onDragEndEntry, origenTipo=null,
  updateEntry, duplicateEntry, deleteEntry, autoCalcHours, soloLectura=false, rellenarDestino=()=>{},
}) {
  // El selector unico de tarea, solo mientras se edita (una peticion para todas las filas, guardada un minuto)
  const poolMisTareas=usePoolMisTareas(isEdit);
  const linkedItem=notionItems.find(t=>t.external_id===a.external_task_id);
  const linkedProj=notionItems.find(p=>p.external_id===a.external_project_id);
  // El paso de proyecto enlazado. Solo se veia al desplegar la fila, asi que
  // desde la lista no habia forma de distinguir una entrada imputada a un paso
  // de una suelta — y son precisamente las horas que alimentan la cronologia.
  const linkedPaso=a.task_id?misPasos.find(s=>String(s.id)===String(a.task_id)):null;
  // El arrastre solo se "arma" desde los tiradores (los 3 puntos o el borde de color).
  // Con draggable en toda la tarjeta el navegador se come la selección de texto de
  // los campos del formulario, así que se activa únicamente al pulsar un tirador.
  const [armed,setArmed]=useState(false);
  // El desglose de la entrada, plegado por defecto: si no se usa, no se nota.
  const subs=resumenSubtareas(a.subtareas);
  const [verSub,setVerSub]=useState(false);
  const dragHandle=draggable?{
    onMouseDown:()=>setArmed(true),
    onMouseUp:()=>setArmed(false),
    onTouchStart:()=>setArmed(true),
    onTouchEnd:()=>setArmed(false),
  }:{};
  return (
    <div draggable={draggable&&armed}
      onDragStart={draggable?onDragStartEntry:undefined}
      onDragEnd={draggable?e=>{setArmed(false);onDragEndEntry?.(e);}:undefined}
      onMouseUp={armed?()=>setArmed(false):undefined}
      onMouseLeave={armed?()=>setArmed(false):undefined}
      className={`cw-entry${isEdit?" cw-active":""}${dragging?" cw-dragging":""}`}
      style={{border:`1px solid color-mix(in srgb, ${col} 45%, transparent)`,cursor:isEdit?"default":"inherit"}}>

      {/* Tirador del borde izquierdo para arrastrar desde ahí */}
      {draggable&&<div className="cw-drag-edge" title="Arrastrar" {...dragHandle}/>}

      {/* Vista compacta. La línea y el panel de subtareas van en un fragmento y
          el onClick se queda SOLO en la línea: con el panel dentro del div
          clicable, marcar una casilla abría el formulario. */}
      {!isEdit&&(
        <>
        <div className="cw-row" style={{display:"flex",alignItems:"center",gap:8}} {...clicable(onOpen,{activo:!soloLectura})}>
          {draggable&&(
            <div title="Arrastrar" {...dragHandle} onClick={e=>e.stopPropagation()}
              style={{cursor:"grab",flexShrink:0,opacity:.3,display:"flex",flexDirection:"column",gap:2,padding:"4px 2px",margin:"-4px -2px"}}>
              {[0,1,2].map(i=><span key={i} style={{width:3,height:3,background:col,borderRadius:"50%",display:"block"}}/>)}
            </div>
          )}
          <span className="cw-date" style={{fontSize:11,color:"var(--text3)",whiteSpace:"nowrap",minWidth:64}}>{fmt(a.start_date||a.entry_date)}</span>
          <span className="cw-mob-hide cw-dep" style={{fontSize:11,color:"var(--text3)",whiteSpace:"nowrap",minWidth:76,overflow:"hidden",textOverflow:"ellipsis"}}>{a.department}</span>
          <span className="cw-title" style={{flex:"1 0 220px",fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5,cursor:"pointer",minWidth:0}}>
            {linkedItem&&<OrigenIcono origen="notion"/>}
            {a.description||<span style={{color:"var(--text3)",fontStyle:"italic",fontWeight:400}}>Sin descripción</span>}
          </span>
          <NotionTag item={linkedItem} parentItem={linkedProj}/>
          {/* Paso del gestor nativo. Si el enlace apunta a un paso borrado no se
              esconde nada: se pinta igual que en el formulario, en ambar.

              Y si el proyecto entero se borro, la fila NO se queda muda: el
              nombre quedo congelado en texto al borrarlo (agenda.project_name y
              task_name), asi que se pinta en gris y con el proyecto delante.
              Estas horas son tuyas y siguen contando; lo unico que ya no existe
              es el sitio al que apuntaban. */}
          {!a.task_id&&(a.task_name||a.project_name)&&(
            <span className="cw-mob-hide cw-chip"
              title={`${a.project_name||"Proyecto"} · el proyecto ya no existe, pero estas horas siguen siendo tuyas`}
              style={{display:"inline-flex",alignItems:"center",gap:4,flexShrink:1,minWidth:0,maxWidth:190,
                fontSize:11,fontWeight:700,padding:"2px 7px",borderRadius:20,whiteSpace:"nowrap",overflow:"hidden",
                background:"var(--surface2)",color:"var(--text3)"}}>
              <Icon name="clock-history" size={9} color="var(--text3)"/>
              <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>
                {[a.project_name,a.task_name].filter(Boolean).join(" · ")}
              </span>
            </span>
          )}
          {a.task_id&&(
            <span className="cw-mob-hide cw-chip"
              title={linkedPaso?`${linkedPaso.project_name} · ${linkedPaso.status}`:"El paso enlazado ya no esta"}
              style={{display:"inline-flex",alignItems:"center",gap:4,flexShrink:1,minWidth:0,maxWidth:190,
                fontSize:11,fontWeight:700,padding:"2px 7px",borderRadius:20,whiteSpace:"nowrap",overflow:"hidden",
                background:linkedPaso?"var(--info-bg)":"var(--warn-bg)",color:linkedPaso?"var(--info-text)":"var(--warn-text)"}}>
              {linkedPaso
                ?<span style={{width:6,height:6,borderRadius:"50%",flexShrink:0,display:"inline-block",
                    background:ST_CLR[linkedPaso.status]||"#6b7280"}}/>
                :<Icon name="exclamation-triangle-fill" size={9} color="var(--warn-text)"/>}
              {linkedPaso&&<OrigenIcono origen="paso" size={9}/>}
              <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>
                {linkedPaso?linkedPaso.title:"Paso borrado"}
              </span>
            </span>
          )}
          {/* La incidencia de la que salio esta entrada. El enlace ya estaba en
              la base de datos desde que existe el cronometro, pero sin pintarlo
              no habia forma de saber a que ticket correspondian estas horas. */}
          {a.mis_tarea_id&&<OrigenIcono origen="suelta" size={10}/>}
          {a.incidencia_id&&(
            <span className="cw-mob-hide cw-chip" title={`Incidencia #${a.incidencia_ref||a.incidencia_id}`}
              style={{display:"inline-flex",alignItems:"center",gap:4,flexShrink:0,fontSize:11,
                fontWeight:700,padding:"2px 7px",borderRadius:20,background:"var(--red-bg)",
                color:"var(--red-text)",whiteSpace:"nowrap"}}>
              <Icon name="headset" size={9} color="#b91c1c"/>#{a.incidencia_ref||a.incidencia_id}
            </span>
          )}
          {/* Esto se esta cronometrando AHORA. `status` no sirve para decirlo:
              se queda en "En progreso" tambien cuando el contador esta parado. */}
          {cronoEstado&&(()=>{
            const marcha=cronoEstado===ESTADO_ACTIVO;
            return (
              <span className="cw-mob-hide"
                title={marcha?"El cronómetro está en marcha":"Cronómetro en pausa"}
                style={{display:"inline-flex",alignItems:"center",gap:5,flexShrink:0,fontSize:11,
                  fontWeight:700,padding:"2px 8px",borderRadius:20,whiteSpace:"nowrap",
                  background:marcha?"var(--ok-bg)":"var(--warn-bg)",color:marcha?"var(--ok-text)":"var(--warn-text)",
                  border:`1px solid ${marcha?"#a7f3d0":"var(--warn-line)"}`}}>
                <span className={marcha?"cw-latido":undefined}
                  style={{width:6,height:6,borderRadius:"50%",display:"inline-block",
                    background:marcha?"#10b981":"#f59e0b"}}/>
                {marcha?"En curso":"En pausa"}
              </span>
            );
          })()}
          {/* Cuántas subtareas hay y cuántas están hechas. Solo si las hay. */}
          {subs.n>0&&(
            <button className="btn cw-mob-hide"
              onClick={e=>{e.stopPropagation();setVerSub(v=>!v);}}
              title={`${subs.hechas} de ${subs.n} subtareas hechas${subs.minutos?` · ${fmtHours(subs.minutos/60)} repartidas`:""}`}
              style={{display:"inline-flex",alignItems:"center",gap:4,flexShrink:0,fontSize:11,
                fontWeight:700,padding:"2px 7px",borderRadius:20,whiteSpace:"nowrap",cursor:"pointer",
                fontFamily:"var(--font)",background:"var(--info-bg)",color:"var(--info-text)",border:"1px solid #c7d2fe"}}>
              <Icon name="check2-square" size={9} color="var(--info-text)"/>
              {subs.hechas}/{subs.n}
              <Icon name={verSub?"chevron-up":"chevron-down"} size={8} color="var(--info-text)"/>
            </button>
          )}
          <span className="cw-mob-hide" style={{fontSize:11,color:"var(--text2)",whiteSpace:"nowrap"}}>{a.recipient||""}</span>
          {/* Prioridad Media y estado Completada son lo habitual: repetirlos en cada
              fila no informa. Se pintan solo cuando se salen de ahí. */}
          {a.priority!=="Media"&&<Badge label={a.priority} color={PR_CLR[a.priority]} bg={PR_BG[a.priority]} className="cw-mob-hide cw-chip"/>}
          {a.status!=="Completada"&&<span className="cw-mob-hide cw-chip" style={{fontSize:11,fontWeight:700,padding:"2px 7px",borderRadius:20,background:(AG_EST_CLR[a.status]||"#9ca3af")+"18",color:AG_EST_CLR[a.status]||"#9ca3af",whiteSpace:"nowrap"}}>{a.status}</span>}
          <span className="cw-hours" style={{fontSize:13,fontWeight:800,color:col,minWidth:36,textAlign:"right",flexShrink:0}}>{fmtHours(a.hours)}</span>
          {!soloLectura&&(<>
          <button aria-label="Duplicar entrada" title="Duplicar entrada" className="btn cw-act" onClick={e=>{e.stopPropagation();duplicateEntry(a);}}
            style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex",background:"none",border:"none",cursor:"pointer",padding:2,display:"flex",flexShrink:0,opacity:.8}}>
            <Icon name="files" size={12} color="var(--text2)"/>
          </button>
          <button aria-label="Eliminar entrada" title="Eliminar entrada" className="btn cw-act" onClick={e=>{e.stopPropagation();deleteEntry(a.id);}}
            style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex",background:"none",border:"none",cursor:"pointer",padding:2,display:"flex",flexShrink:0,opacity:.8}}>
            <Icon name="trash3" size={12} color="var(--red-text)"/>
          </button>
          </>)}
        </div>

        {/* El desglose, sin abrir el formulario. Las casillas funcionan desde
            aquí: marcar una subtarea hecha es el gesto más frecuente. */}
        {verSub&&subs.n>0&&(
          <div style={{marginTop:7,marginLeft:6,paddingLeft:10,borderLeft:`2px solid ${col}55`,
            display:"flex",flexDirection:"column",gap:3}}>
            {normalizaSubtareas(a.subtareas).map(s=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:7,fontSize:11.5}}>
                <input aria-label={s.texto} type="checkbox" checked={s.hecha} disabled={soloLectura} style={{cursor:soloLectura?"default":"pointer",flexShrink:0}}
                  onChange={()=>{if(!soloLectura) updateEntry(a.id,{subtareas:toggleSubtarea(a.subtareas,s.id)});}}/>
                <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap",color:s.hecha?"var(--text3)":"var(--text2)",
                  textDecoration:s.hecha?"line-through":"none"}}>
                  {s.texto}
                </span>
                <span style={{flexShrink:0,fontSize:11,fontWeight:700,
                  color:s.minutos?"var(--text2)":"var(--text3)"}}>
                  {s.minutos?fmtHours(s.minutos/60):"—"}
                </span>
              </div>
            ))}
          </div>
        )}
        </>
      )}

      {/* Formulario expandido */}
      {isEdit&&(
        <div>
          {/* Fila 1: tipo | tiempo */}
          <div className="cw-f1">
            <div className="cw-field">
              <Campo><label>Tipo{!a.type&&<span style={{color:"var(--red-text)",fontWeight:700}}> · sin asignar</span>}</label>
              <select value={a.type||""} onChange={e=>updateEntry(a.id,{type:e.target.value||null})}
                style={!a.type?{borderColor:"var(--red)"}:undefined}>
                {!a.type&&<option value="">— Elige un tipo —</option>}
                {a.type&&!activeTipos.includes(a.type)&&(
                  <option value={a.type}>{a.type} {archivedTipos.includes(a.type)?"(archivado)":"(tipo eliminado)"}</option>
                )}
                {activeTipos.map(t=><option key={t}>{t}</option>)}
              </select></Campo>
            </div>
            <div className="cw-field"><Campo><label>Tiempo m/h</label>
              <HoursInput value={a.hours} onChange={v=>updateEntry(a.id,{hours:v})}/></Campo>
            </div>
          </div>
          {/* Fila 2: [fecha + hora de inicio] | [fecha + hora de fin] */}
          <div className="cw-f2">
            <div className="cw-field"><Campo><label>Inicio</label>
              <div className="cw-fh">
                <DatePicker
                  value={(a.start_date||a.entry_date||"").slice(0,10)}
                  onChange={v=>{
                    updateEntry(a.id,{start_date:v||null,entry_date:v||a.entry_date});
                    autoCalcHours(v,editTimes.start,a.end_date,editTimes.end);
                  }}
                  placeholder="Inicio" nombre="Fecha de inicio" margenInferior={0}
                />
                <HoraInput aria-label="Hora de inicio (24 horas)" value={editTimes.start||""}
                  onChange={t=>{
                    const next={...editTimes,start:t};
                    setEditTimes(next);
                    updateEntry(a.id,{start_time:t||null});
                    autoCalcHours(a.start_date||a.entry_date,t,a.end_date,next.end);
                  }}/>
              </div></Campo>
            </div>
            <div className="cw-field"><Campo><label>Fin</label>
              <div className="cw-fh">
                <DatePicker
                  value={(a.end_date||"").slice(0,10)}
                  onChange={v=>{
                    updateEntry(a.id,{end_date:v||null});
                    autoCalcHours(a.start_date||a.entry_date,editTimes.start,v,editTimes.end);
                  }}
                  placeholder="Fin" nombre="Fecha de fin" margenInferior={0}
                />
                <HoraInput aria-label="Hora de fin (24 horas)" value={editTimes.end||""}
                  onChange={t=>{
                    const next={...editTimes,end:t};
                    setEditTimes(next);
                    updateEntry(a.id,{end_time:t||null});
                    autoCalcHours(a.start_date||a.entry_date,next.start,a.end_date,t);
                  }}/>
              </div></Campo>
            </div>
          </div>
          {/* Aviso de día no laborable — informativo, nunca bloquea el guardado */}
          {(()=>{
            const d=(a.start_date||a.entry_date||"").slice(0,10);
            if(!d||!esNoLaborable(d,festivos)) return null;
            return (
              <div style={{display:"flex",alignItems:"center",gap:6,margin:"-4px 0 10px",padding:"6px 9px",
                borderRadius:8,background:"var(--warn-bg)",border:"1px solid var(--warn-line)",fontSize:11,color:"var(--warn-text)"}}>
                <Icon name="exclamation-triangle-fill" size={11} color="var(--warn-text)"/>
                {festivos[d] ? `Festivo: ${festivos[d]}` : "Fin de semana"} · imputas horas en un día no laborable
              </div>
            );
          })()}
          {/* Fila 3: descripción | asignación */}
          <div className="cw-desc-grid" style={{display:"grid",gridTemplateColumns:"minmax(0,11fr) minmax(0,9fr)",gap:10,marginBottom:10,alignItems:"start"}}>
            <div className="cw-field" style={{marginBottom:0}}>
              <Campo><label>Descripción</label>
              <input value={a.description||""}
                onChange={e=>updateEntry(a.id,{description:e.target.value})}
                placeholder="Descripción de la tarea…"
                style={{width:"100%",padding:"7px 10px",borderRadius:8,border:"1px solid var(--border)",fontSize:12.5,fontFamily:"var(--font)",background:"var(--surface2)",color:"var(--text)"}}/></Campo>
            </div>
            <div className="cw-field" style={{marginBottom:0}}>
              {/* UN campo para las tres clases de asignacion, con pestañas.
                  Antes eran tres campos y solo uno podia estar puesto, asi que
                  dos estaban siempre vacios ocupando sitio. Las columnas de
                  debajo siguen siendo las mismas: ver SelectorAsignacion. */}
              <Campo><label>Asignación</label>
              <SelectorAsignacion
                entry={a} misPasos={misPasos} notionItems={notionItems}
                poolMisTareas={poolMisTareas}
                verIncidencias={canVerIncidencias}
                onAbrirIncidencia={onAbrirIncidencia} origenInicial={origenTipo}
                onChange={patch=>{ updateEntry(a.id,patch); if(patch.incidencia_id) rellenarDestino(a.id, patch.incidencia_id); }}/></Campo>
            </div>
          </div>
          {/* Fila 4: destinatario | equipo | prioridad | estado */}
          <div className="cw-f4">
            <div className="cw-field">
              <Campo><label>Destinatario</label>
              <RecipientInput value={a.recipient||""}
                onChange={v=>updateEntry(a.id,{recipient:v})}
                onSelectUser={u=>{
                  // Al elegir una persona, autoasigna su equipo (si tiene uno)
                  const team=(teams||[]).find(t=>(t.members||[]).map(String).includes(String(u.id)));
                  updateEntry(a.id, team ? {recipient:u.name, department:team.name} : {recipient:u.name});
                }}
                allUsers={allUsers}/></Campo>
            </div>
            <div className="cw-field"><Campo><label>Equipo</label>
              <select value={a.department} onChange={e=>updateEntry(a.id,{department:e.target.value})}>
                {(teams||[]).map(t=><option key={t.id}>{t.name}</option>)}
              </select></Campo>
            </div>
            <div className="cw-field"><Campo><label>Prioridad</label>
              <select value={a.priority} onChange={e=>updateEntry(a.id,{priority:e.target.value})}>
                {AG_PRIOS.map(p=><option key={p}>{p}</option>)}
              </select></Campo>
            </div>
            <div className="cw-field"><Campo><label>Estado</label>
              <select value={a.status} onChange={e=>updateEntry(a.id,{status:e.target.value})}>
                {AG_ESTADOS.map(s=><option key={s}>{s}</option>)}
              </select></Campo>
            </div>
          </div>
          {/* Subtareas. Van pegadas a la fila de Tiempo porque el aviso de
              descuadre habla justo de ese número. */}
          <SubtareasEditor
            items={a.subtareas}
            minutosEntrada={Math.round((parseFloat(a.hours)||0)*60)}
            onChange={(next,{texto})=>updateEntry(a.id,{subtareas:next},{debounce:texto})}
            // Manda `hours` ADEMÁS de `minutes`: el servidor se apaña solo con
            // cualquiera de los dos, pero la pantalla pinta `a.hours`, y sin él
            // el ajuste no se vería hasta recargar.
            onAjustarTiempo={min=>updateEntry(a.id,{
              minutes: min,
              hours: Math.round(min/60*100)/100,
            })}
          />
          <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"flex-end"}}>
            {linkedProj&&<a href={linkedProj.url||"#"} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,background:"var(--info-bg)",color:"var(--info-text)",marginRight:"auto",display:"flex",alignItems:"center",gap:4,textDecoration:"none"}}>
              <Icon name="box-arrow-up-right" size={10} color="var(--info-text)"/>{linkedProj.title}
            </a>}
            <button className="btn" onClick={()=>duplicateEntry(a)}
              style={{padding:"5px 10px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text2)",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:4}}>
              <Icon name="files" size={11} color="var(--text2)"/>Duplicar
            </button>
            <button className="btn" onClick={()=>deleteEntry(a.id)}
              style={{padding:"5px 10px",borderRadius:8,border:"1px solid var(--red-line)",background:"var(--red-bg)",color:"var(--red-text)",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:4}}>
              <Icon name="trash3" size={11} color="var(--red-text)"/>Eliminar
            </button>
            <button className="btn" onClick={onCloseEdit}
              style={{padding:"5px 14px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#086C76,#087DAB)",color:"#fff",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
              <Icon name="check2" size={12} color="#fff"/>Listo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CuadernoPage({
  agenda, setAgenda, notionItems=[], misPasos=[], prefill, clearPrefill,
  currentUser, allUsers=[], teams=[], showToast=()=>{}, festivos={},
  canVerIncidencias=false, crono=null, onAbrirIncidencia,
  onCreateEntry, onUpdateEntry, onDeleteEntry, agendaTipos=[], setAgendaTipos,
  agendaTiposData=[], setAgendaTiposData, loadAgendaTipos,
  loadAgenda,
  onExportPdf,
  onImport,
  onViewingUserChange,
}) {
  const isManagerRole = esAdmin(currentUser);

  // Que entrada esta ahora mismo bajo el cronometro. Se cruza por `agenda_id`,
  // que es lo que el cronometro guarda de la entrada que va escribiendo. El
  // campo `status` no sirve para esto: se queda en "En progreso" tambien cuando
  // el contador esta parado, asi que no distingue lo vivo de lo dejado a medias.
  const cronoDe = id => {
    if (!crono) return null;
    if (crono.activo?.agenda_id && String(crono.activo.agenda_id)===String(id)) return "activo";
    return (crono.pausados||[]).some(p => String(p.agenda_id)===String(id)) ? "pausado" : null;
  };
  const myFirstTeam   = teams.find(t=>(t.members||[]).map(String).includes(String(currentUser?.id)));

  const [agTab,          setAgTab         ] = useState("tabla");

  const _sf = useMemo(()=>{ try{return JSON.parse(sessionStorage.getItem('cuaderno_filters'))||{};}catch{return {};} },[]);
  const [filterPer,      setFilterPer     ] = useState(_sf.filterPer==="ayer" ? "ultimo_dia" : (_sf.filterPer ?? "hoy"));
  const [filterMes,      setFilterMes     ] = useState(_sf.filterMes    ?? "");
  const [filterDesde,    setFilterDesde   ] = useState(_sf.filterDesde  ?? "");
  const [filterHasta,    setFilterHasta   ] = useState(_sf.filterHasta  ?? "");
  const [filterTipo,     setFilterTipo    ] = useState(_sf.filterTipo   ?? "");
  const [filterDpto,     setFilterDpto    ] = useState(_sf.filterDpto   ?? "");
  const [filterQ,        setFilterQ       ] = useState(_sf.filterQ      ?? "");
  const [filterNotion,   setFilterNotion  ] = useState(_sf.filterNotion ?? "");
  const [collapsed,      setCollapsed     ] = useState(new Set());
  const [editId,         setEditId        ] = useState(null);
  const [editTimes,      setEditTimes     ] = useState({start:"",end:""});
  const [showTypeMgr,    setShowTypeMgr   ] = useState(false);
  const [dragEntryId,    setDragEntryId   ] = useState(null);
  const [dragOverTipo,   setDragOverTipo  ] = useState(null);
  const [dragGrpIdx,     setDragGrpIdx    ] = useState(null);
  const [dragOverGrpIdx, setDragOverGrpIdx] = useState(null);
  const [tipoModal,      setTipoModal     ] = useState(null);
  const [viewUserId,     setViewUserId    ] = useState(null);
  const [viewTeamId,     setViewTeamId    ] = useState(myFirstTeam?.id ? String(myFirstTeam.id) : null);
  const [showAdminBanner,setShowAdminBanner] = useState(false);
  // Pie de horas en movil: una linea compacta; al tocarla se despliega el detalle por tipo.
  const [verDetallePie,setVerDetallePie] = useState(false);
  // Barra de iconos movil: que panel de filtros / vista esta desplegado bajo la barra.
  const [mobFiltros,setMobFiltros] = useState(false);
  const [mobVista,setMobVista] = useState(false);
  const [prodEquipo,     setProdEquipo     ] = useState(null);   // comparativa admin: null = sin cargar
  const newRef          = useRef(null);
  const pendingPatches  = useRef({});
  const pendingTimers   = useRef({});
  const effectiveUserId = viewUserId || currentUser?.id;
  // El gestor consulta el cuaderno de su gente pero no lo toca: solo el admin escribe
  // en el de otra persona (espejo del 403/404 de backend/routes/agenda.js).
  const soloLectura = !!viewUserId && String(viewUserId)!==String(currentUser?.id) && !esAdmin(currentUser);

  // Avisa a la cabecera global de qué cuaderno se está viendo (null = el propio)
  useEffect(()=>{
    if(!onViewingUserChange) return;
    const u = allUsers.find(x=>String(x.id)===String(effectiveUserId));
    onViewingUserChange(u && String(u.id)!==String(currentUser?.id) ? u : null);
    return ()=>onViewingUserChange(null);
  }, [effectiveUserId, allUsers, currentUser?.id]); // eslint-disable-line

  const weekRange = () => {
    const d=new Date(); const day=d.getDay(); const diff=day===0?-6:1-day;
    const lun=new Date(d); lun.setDate(d.getDate()+diff);
    const dom=new Date(lun); dom.setDate(lun.getDate()+6);
    return [lun.toISOString().slice(0,10), dom.toISOString().slice(0,10)];
  };
  const prevWeekRange = () => {
    const d=new Date(); const day=d.getDay(); const diff=day===0?-6:1-day;
    const lun=new Date(d); lun.setDate(d.getDate()+diff-7);
    const dom=new Date(lun); dom.setDate(lun.getDate()+6);
    return [lun.toISOString().slice(0,10), dom.toISOString().slice(0,10)];
  };

  const entryDate = a => (a.entry_date || a.start_date || "").slice(0,10);

  const allMonths = [...new Set(
    agenda.filter(a=>String(a.user_id)===String(effectiveUserId))
      .map(a=>entryDate(a).slice(0,7)).filter(Boolean)
  )].sort();

  const myEntries = agenda.filter(a=>String(a.user_id)===String(effectiveUserId));

  // Último día con datos: la fecha más reciente anterior a hoy que tiene entradas
  const lastDataDay = useMemo(()=>{
    const hoy=today();
    const dates = myEntries.map(entryDate).filter(d=>d && d < hoy);
    return dates.length ? dates.sort().at(-1) : null;
  },[myEntries]); // eslint-disable-line

  // Día único del filtro activo: si la vista está acotada a un solo día, las
  // entradas nuevas nacen con esa fecha en vez de con hoy.
  const filterDay = useMemo(()=>{
    if(filterDesde && filterHasta && filterDesde===filterHasta) return filterDesde;
    if(filterDesde || filterHasta) return null;
    if(filterPer==="hoy")        return today();
    if(filterPer==="ultimo_dia") return lastDataDay || null;
    return null;
  },[filterPer,filterDesde,filterHasta,lastDataDay]);

  const filteredNoNotion = myEntries.filter(a=>{
    // La entrada en edición nunca se filtra — evita que desaparezca al cambiar campos
    if(String(a.id)===String(editId)) return true;
    const hoy=today(); const [lun,dom]=weekRange(); const [plun,pdom]=prevWeekRange(); const ed=entryDate(a);
    if(filterPer==="hoy"            && ed!==hoy) return false;
    if(filterPer==="ultimo_dia"     && (!lastDataDay || ed!==lastDataDay)) return false;
    if(filterPer==="semana"         && (!ed||ed<lun||ed>dom)) return false;
    if(filterPer==="semana_anterior"&& (!ed||ed<plun||ed>pdom)) return false;
    if(filterDesde && (!ed||ed<filterDesde)) return false;
    if(filterHasta && (!ed||ed>filterHasta)) return false;
    if(filterMes  && !ed.startsWith(filterMes)) return false;
    if(filterTipo && a.type!==filterTipo) return false;
    if(filterDpto && a.department!==filterDpto) return false;
    if(filterQ    && !a.description?.toLowerCase().includes(filterQ.toLowerCase())
                  && !(a.recipient||"").toLowerCase().includes(filterQ.toLowerCase())) return false;
    return true;
  });

  // Pestañas de proyecto Notion: una por página vinculada en las entradas visibles
  const notionTabs = useMemo(()=>{
    const map = new Map();
    filteredNoNotion.forEach(a=>{
      if(!a.external_task_id) return;
      const item = notionItems.find(i=>i.external_id===a.external_task_id);
      if(!item) return;
      const prev = map.get(item.external_id) || { item, count:0, hours:0 };
      prev.count += 1;
      prev.hours += parseFloat(a.hours)||0;
      map.set(item.external_id, prev);
    });
    return [...map.values()].sort((x,y)=>y.hours-x.hours);
  },[filteredNoNotion,notionItems]); // eslint-disable-line

  // Si el proyecto filtrado deja de existir en la vista, se limpia el filtro
  useEffect(()=>{
    if(filterNotion && !notionTabs.some(t=>t.item.external_id===filterNotion)) setFilterNotion("");
  },[notionTabs,filterNotion]); // eslint-disable-line

  const filtered = filterNotion
    ? filteredNoNotion.filter(a=>a.external_task_id===filterNotion||String(a.id)===String(editId))
    : filteredNoNotion;

  // Tipos activos vs. archivados: un tipo archivado sale de los desplegables pero
  // sigue agrupando y sumando mientras conserve entradas, para que las horas del
  // histórico no desaparezcan de los totales ni del PDF.
  const tipoRow       = tp => agendaTiposData.find(t=>t.name===tp);
  // La asignación predeterminada del tipo, que sale preseleccionada en una entrada sin enlace
  const origenDe      = tp => { const o=agOrigen(tipoRow(tp)); return o==='ninguno'?null:o; };
  const activeTipos   = agendaTipos.filter(tp=>!tipoRow(tp)?.archived);
  const archivedTipos = agendaTipos.filter(tp=>!!tipoRow(tp)?.archived);
  const groupTipos    = [...activeTipos, ...archivedTipos.filter(tp=>filtered.some(a=>a.type===tp))];

  // Uso real sobre TODAS las entradas del usuario, no sobre el filtro activo:
  // es lo que decide si un tipo puede borrarse sin dejar entradas huérfanas.
  const tiposUsage  = useMemo(()=>tiposUsageMap(myEntries),[myEntries]); // eslint-disable-line

  const totalH      = filtered.reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
  const totalByTipo = groupTipos.reduce((acc,tp)=>({
    ...acc,[tp]:filtered.filter(a=>a.type===tp).reduce((s,a)=>s+(parseFloat(a.hours)||0),0)
  }),{});

  useEffect(()=>{
    sessionStorage.setItem('cuaderno_filters', JSON.stringify({filterPer,filterMes,filterDesde,filterHasta,filterTipo,filterDpto,filterQ,filterNotion}));
  },[filterPer,filterMes,filterDesde,filterHasta,filterTipo,filterDpto,filterQ,filterNotion]);

  useEffect(()=>{
    if(prefill){ addRow(prefill); clearPrefill(); setFilterPer(""); }
  },[prefill]);

  const addRow = async (prefillData={}) => {
    if(!onCreateEntry||soloLectura) return;
    const data = {
      user_id:     effectiveUserId,
      entry_date:  prefillData.entry_date  || filterDay || today(),
      start_date:  prefillData.start_date  || filterDay || today(),
      end_date:    prefillData.end_date    || null,
      type:        prefillData.type        || activeTipos[0] || "Tarea Principal",
      // El equipo de quien apunta, no el primero de la lista: escribiendo sobre
      // otra persona vale el suyo, que es de quien sera la entrada.
      department:  prefillData.department  ||
                   teams.find(t=>(t.members||[]).map(String).includes(String(effectiveUserId)))?.name ||
                   myFirstTeam?.name || "",
      description: prefillData.description || "",
      recipient:   prefillData.recipient   || "",
      hours:       prefillData.hours       || 0.5,
      priority:    prefillData.priority    || "Media",
      status:      prefillData.status      || "Completada",
      external_task_id:    prefillData.external_task_id    || null,
      external_project_id: prefillData.external_project_id || null,
      subtareas:   prefillData.subtareas   || [],
    };
    const saved = await onCreateEntry(data);
    if(saved) {
      setEditId(String(saved.id));
      setTimeout(()=>newRef.current?.scrollIntoView({behavior:"smooth",block:"start"}),100);
    }
  };

  const openEntry = (a) => {
    if(soloLectura) return;
    setEditId(a.id);
    setEditTimes({start:a.start_time?.slice(0,5)||"", end:a.end_time?.slice(0,5)||""});
  };

  // Vacía los parches acumulados de una entrada (o de todas) sin esperar a que
  // venza el temporizador. Sin esto, teclear y cerrar la fila antes de los
  // 600 ms pierde lo último escrito.
  const flushPending = (id=null) => {
    const ids = id!==null ? [String(id)] : Object.keys(pendingPatches.current);
    ids.forEach(k=>{
      const patch = pendingPatches.current[k];
      if(!patch) return;
      clearTimeout(pendingTimers.current[k]);
      delete pendingPatches.current[k];
      onUpdateEntry?.(k, patch);
    });
  };

  // Al desmontar la página se envía lo que quedara a medias: cambiar de pestaña
  // no debería costar la última palabra.
  useEffect(()=>()=>flushPending(), []); // eslint-disable-line

  // `opts.debounce` permite pedirlo explícitamente: el texto de una subtarea se
  // teclea, pero marcar su casilla no, y las dos cosas viajan en el mismo campo.
  // Sin el tercer argumento el comportamiento es el de siempre.
  // Al elegir una incidencia a mano, el destinatario y el equipo salen del contacto del ticket (solo si el destinatario esta
  // vacio): ver src/destinoIncidencia.js. `agendaRef` da la entrada ACTUAL cuando llega la respuesta.
  const agendaRef = useRef(agenda);
  agendaRef.current = agenda;
  const rellenarDestino = (id, incidenciaId) => rellenarDestinoDeIncidencia({
    incidenciaId,
    actual: () => agendaRef.current.find(x => String(x.id) === String(id)),
    buscar: api.destinoIncidencia,
    aplicar: patch => updateEntry(id, patch),
    avisar: m => showToast(m, "warn"),
  });
  const updateEntry = (id, patch, opts) => {
    setAgenda(p=>p.map(a=>String(a.id)===String(id)?{...a,...patch}:a));
    if(!onUpdateEntry || isNaN(id)) return;
    const isTextField = opts?.debounce ?? ("description" in patch || "recipient" in patch);
    if(isTextField) {
      // Acumular parches de texto y enviar solo cuando el usuario deja de escribir
      pendingPatches.current[id] = { ...(pendingPatches.current[id]||{}), ...patch };
      clearTimeout(pendingTimers.current[id]);
      pendingTimers.current[id] = setTimeout(() => {
        onUpdateEntry(id, pendingPatches.current[id]);
        delete pendingPatches.current[id];
      }, 600);
    } else {
      // Campos de selección: flush pendientes + envío inmediato
      if(pendingPatches.current[id]) {
        clearTimeout(pendingTimers.current[id]);
        onUpdateEntry(id, { ...pendingPatches.current[id], ...patch });
        delete pendingPatches.current[id];
      } else {
        onUpdateEntry(id, patch);
      }
    }
  };

  const workingHours = (start, end) => {
    // Cuenta solo horas laborales L-V entre 08:00 y 17:00
    if (end <= start) return 0;
    let mins = 0;
    const cur = new Date(start); cur.setHours(0,0,0,0);
    while (cur <= end) {
      const dow = cur.getDay();
      if (dow >= 1 && dow <= 5) { // lunes-viernes
        const ws = new Date(cur); ws.setHours(8,0,0,0);
        const we = new Date(cur); we.setHours(17,0,0,0);
        const s = Math.max(start.getTime(), ws.getTime());
        const e = Math.min(end.getTime(),   we.getTime());
        if (e > s) mins += (e - s) / 60000;
      }
      cur.setDate(cur.getDate()+1);
    }
    return mins / 60;
  };

  const autoCalcHours = (startDate, startTime, endDate, endTime) => {
    const sd = startDate || null;
    const ed = endDate || sd;
    if (!sd || !startTime || !endTime) return;
    const start = new Date(`${String(sd).slice(0,10)}T${startTime}`);
    const end   = new Date(`${String(ed).slice(0,10)}T${endTime}`);
    const diff  = workingHours(start, end);
    if (diff > 0) updateEntry(editId, { hours: Math.round(diff * 60) / 60 });
  };

  const [pedirBorrado, dialogoBorrado] = useConfirmar();
  const deleteEntry = async (id) => {
    const entrada = agenda.find(a=>String(a.id)===String(id));
    if(!await pedirBorrado({
      titulo:"¿Eliminar la entrada?",
      mensaje: entrada ? `«${entrada.description||"Sin descripción"}» · ${entrada.hours}h` : undefined,
      detalle:"Las horas dejan de contar en el cuaderno. No se puede deshacer.",
      etiquetaOk:"Eliminar", peligro:true,
    })) return;
    setAgenda(p=>p.filter(a=>String(a.id)!==String(id)));
    if(String(editId)===String(id)) setEditId(null);
    if(onDeleteEntry && !isNaN(id)) onDeleteEntry(id);
  };

  const duplicateEntry = (a) => {
    addRow({
      entry_date:          a.entry_date,
      start_date:          a.start_date,
      end_date:            a.end_date,
      start_time:          a.start_time,
      end_time:            a.end_time,
      type:                a.type,
      department:          a.department,
      description:         a.description,
      recipient:           a.recipient,
      hours:               a.hours,
      priority:            a.priority,
      status:              a.status,
      external_task_id:    a.external_task_id,
      external_project_id: a.external_project_id,
      // Se copia la plantilla, no el progreso: duplicar sirve para repetir una
      // tarea, y arrastrar las casillas que se marcaron ayer sería mentira. Los
      // minutos sí se copian, que son la estimación y es justo lo que se
      // reaprovecha. Ids nuevos para que las dos entradas no compartan claves.
      subtareas: normalizaSubtareas(a.subtareas).map(s=>({...s, id:nuevoIdSubtarea(), hecha:false})),
    });
  };

  // Drag & drop entradas
  const onDragStart    = (id)    => setDragEntryId(id);
  const onDragOverTipo = (tp,e)  => { e.preventDefault(); setDragOverTipo(tp); };
  const onDropOnTipo   = (tp)    => { if(!dragEntryId) return; updateEntry(dragEntryId,{type:tp}); setDragEntryId(null); setDragOverTipo(null); };

  // Drag & drop grupos
  const onGrpDragStart = (idx,e) => { e.stopPropagation(); setDragGrpIdx(idx); };
  const onGrpDragOver  = (idx,e) => { e.preventDefault(); e.stopPropagation(); setDragOverGrpIdx(idx); };
  const onGrpDrop      = (idx)   => {
    if(dragGrpIdx===null||dragGrpIdx===idx){setDragGrpIdx(null);setDragOverGrpIdx(null);return;}
    // Los índices son de la lista de tipos activos; los archivados van al final
    const act=[...activeTipos]; const [moved]=act.splice(dragGrpIdx,1); act.splice(idx,0,moved);
    const next=[...act,...archivedTipos];
    setAgendaTipos(next);
    setAgendaTiposData(p=>p.map(t=>{const i=next.indexOf(t.name);return i>=0?{...t,sort_order:i+1}:t;}));
    setDragGrpIdx(null); setDragOverGrpIdx(null);
    // El orden se persiste: antes solo vivía en el estado y se perdía al recargar
    const ids=next.map(n=>agendaTiposData.find(t=>t.name===n)?.id).filter(Boolean);
    if(ids.length) api.reorderAgendaTipos(ids,effectiveUserId).catch(e=>showToast(e.message||"No se pudo guardar el orden","error"));
  };

  // Recargar entradas y tipos al cambiar el usuario visualizado (vista admin)
  useEffect(() => {
    if(loadAgenda) loadAgenda(viewUserId || undefined);
    if(loadAgendaTipos) loadAgendaTipos(viewUserId || undefined);
  }, [viewUserId]); // eslint-disable-line

  // Rango de fechas que abarca el filtro actual. La comparativa del equipo se
  // resuelve en el backend y solo entiende de fechas: GET /agenda devuelve un
  // cuaderno a la vez, así que sin ese endpoint solo se podría comparar a la
  // gente cuyo cuaderno ya se hubiera abierto en esta sesión.
  const rangoFiltrado = useMemo(()=>{
    const ds=filtered.map(entryDate).filter(Boolean).sort();
    return ds.length ? {desde:ds[0],hasta:ds[ds.length-1]} : null;
  },[filtered]); // eslint-disable-line

  useEffect(() => {
    if(!isManagerRole || agTab!=="dash" || !rangoFiltrado) { setProdEquipo(null); return; }
    let vivo=true;
    api.getAgendaProductividad(rangoFiltrado)
      .then(rows=>{ if(vivo&&Array.isArray(rows)) setProdEquipo(rows); })
      .catch(()=>{ if(vivo) setProdEquipo([]); });
    return ()=>{ vivo=false; };
  }, [isManagerRole, agTab, rangoFiltrado?.desde, rangoFiltrado?.hasta]); // eslint-disable-line

  // ── Gestión de tipos ────────────────────────────────────────────────────────
  // El vínculo entrada→tipo es el NOMBRE (agenda.type), no el id. Renombrar o
  // eliminar un tipo tiene que arrastrar sus entradas: el backend lo hace en
  // transacción y aquí se replica en el estado local para no esperar recarga.
  const saveTipo = async (tipo, patch) => {
    const name = (patch.name||"").trim();
    if(!name) return;
    try {
      if(tipo?.id) {
        // El peso solo se envía si el modal lo ofrecía (admin): mandarlo desde un
        // usuario normal haría que el backend rechazara todo el cambio con 403.
        const prodPatch = patch.productividad !== undefined ? { productividad: patch.productividad } : {};
        const row = await api.updateAgendaTipo(tipo.id, { name, color: patch.color, icon: patch.icon||null, ...prodPatch });
        const merged = row?.id ? row : { ...tipo, name, color: patch.color, icon: patch.icon||null, ...prodPatch };
        setAgendaTiposData(p => p.map(t => t.id === tipo.id ? { ...t, ...merged } : t));
        if(name !== tipo.name) {
          setAgendaTipos(p => p.map(t => t === tipo.name ? name : t));
          setAgenda(p => p.map(a => a.type === tipo.name ? { ...a, type: name } : a));
          if(filterTipo === tipo.name) setFilterTipo(name);
          // El grupo colapsado se identifica por nombre: hay que reetiquetarlo
          setCollapsed(prev => {
            if(!prev.has(tipo.name)) return prev;
            const n = new Set(prev); n.delete(tipo.name); n.add(name); return n;
          });
          showToast(`Tipo renombrado a "${name}"${row?.renamed_entries ? ` · ${row.renamed_entries} entradas actualizadas` : ""}`);
        } else {
          showToast(`Tipo "${name}" actualizado`);
        }
      } else {
        const row = await api.createAgendaTipo({ name, color: patch.color, icon: patch.icon||null, user_id: effectiveUserId,
          ...(patch.productividad !== undefined ? { productividad: patch.productividad } : {}) });
        if(!row?.id) throw new Error("No se pudo crear el tipo");
        setAgendaTiposData(p => [...p, row]);
        setAgendaTipos(p => [...p, row.name]);
        showToast(`Tipo "${row.name}" creado`);
      }
      setTipoModal(null);
    } catch(e) {
      showToast(e.message || "Error al guardar el tipo", "error");
    }
  };

  // mode: archive | unarchive | reassign | delete
  const deleteTipo = async (tipo, opts={}) => {
    if(!tipo?.id) return;
    try {
      if(opts.mode === "archive" || opts.mode === "unarchive") {
        const archived = opts.mode === "archive";
        const row = await api.updateAgendaTipo(tipo.id, { archived });
        setAgendaTiposData(p => p.map(t => t.id === tipo.id ? { ...t, ...(row?.id ? row : {}), archived } : t));
        if(archived && filterTipo === tipo.name) setFilterTipo("");
        showToast(archived ? `Tipo "${tipo.name}" archivado — su histórico se conserva`
                           : `Tipo "${tipo.name}" restaurado`);
        setTipoModal(null);
        return;
      }
      const res = await api.deleteAgendaTipo(tipo.id,
        opts.mode === "reassign" ? { reassign_to: opts.reassignTo } : { force: true });
      setAgendaTiposData(p => p.filter(t => t.id !== tipo.id));
      setAgendaTipos(p => p.filter(t => t !== tipo.name));
      if(opts.mode === "reassign") {
        setAgenda(p => p.map(a => a.type === tipo.name ? { ...a, type: opts.reassignTo } : a));
        showToast(`${res?.moved_entries ?? 0} entrada(s) movidas a "${opts.reassignTo}"`);
      } else {
        // Sin tipo: quedan visibles en el grupo "Otros", desde donde se reasignan
        setAgenda(p => p.map(a => a.type === tipo.name ? { ...a, type: null } : a));
        const moved = res?.moved_entries || 0;
        showToast(moved ? `Tipo eliminado · ${moved} entrada(s) en "Otros"` : `Tipo "${tipo.name}" eliminado`);
      }
      if(filterTipo === tipo.name) setFilterTipo("");
      setTipoModal(null);
    } catch(e) {
      showToast(e.message || "Error al eliminar el tipo", "error");
    }
  };

  // Las tarjetas de tipo salen de los tipos con más horas del filtro actual.
  // Antes estaban cableadas a "Interrupciones"/"Reuniones" y se quedaban a cero
  // en cuanto alguien renombraba o eliminaba esos tipos.
  const topTipos = groupTipos
    .map(tp=>({tp,hours:totalByTipo[tp]||0,count:filtered.filter(a=>a.type===tp).length}))
    .filter(t=>t.hours>0)
    .sort((a,b)=>b.hours-a.hours)
    .slice(0,3);

  // Índice de productividad del período filtrado: las horas de cada entrada
  // ponderadas por el peso de su tipo. Es el KPI que abre el dashboard.
  const prod = useMemo(()=>prodStats(filtered,agendaTiposData),[filtered,agendaTiposData]);
  const prodBandActual = prod.index===null ? null : prodBand(prod.index);

  const kpis = [
    ...(prod.index!==null ? [[
      "graph-up-arrow",
      prodBandActual.color,
      prod.index+"%",
      "Productividad",
      // La relación alta:baja es lo que da contexto al %: un 70% con muchas
      // horas de bajo valor no significa lo mismo que un 70% sin ninguna.
      fmtRatio(prod.ratio)+" alta:baja",
    ]] : []),
    ["clock","var(--accent)",fmtHours(totalH),"Horas totales",filtered.length+" registros"],
    ...topTipos.map(t=>[
      getTipoIcon(t.tp,agendaTiposData) || "lightning-fill",
      getTipoColor(t.tp,agendaTipos,agendaTiposData),
      fmtHours(t.hours),
      t.tp,
      t.count+(t.count===1?" entrada":" entradas"),
    ]),
  ];

  const selS = {padding:"5px 9px",borderRadius:7,border:"1px solid var(--border)",fontSize:11.5,color:"var(--text2)",background:"var(--surface2)",cursor:"pointer",fontFamily:"var(--font)"};
  const isAdminView = puedeVerCuadernoAjeno(currentUser);
  const cuadernosVer = cuadernosVisiblesPor(currentUser, allUsers, teams);
  const equiposVer = esSuper(currentUser) ? (teams||[]) : equiposDe(currentUser, teams);
  const viewingUser = allUsers.find(u=>String(u.id)===String(effectiveUserId));
  const isViewingOther = !!(viewingUser && String(viewingUser.id)!==String(currentUser?.id));
  // Filtros activos, para el numerito del icono de la barra movil.
  const nFiltros = [filterTipo,filterDpto,filterNotion,filterQ].filter(Boolean).length
    + ((filterPer&&filterPer!=="hoy")||filterMes||filterDesde||filterHasta ? 1 : 0);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",position:"relative"}}>
      {dialogoBorrado}

      {/* Barra movil (<=600px): solo iconos, cada uno con su aria-label. Los paneles
          (Vista, Cuaderno de otra persona, Filtros, Tipos) se despliegan debajo, sin
          tapar la lista. En escritorio no existe: ahi siguen las barras de siempre. */}
      <div className="cw-tb cw-mob-only" role="toolbar" aria-label="Acciones del Cuaderno">
        {!soloLectura&&<BtnPrimary onClick={()=>addRow()}>
          <Icon name="plus-lg" size={13} color="#fff"/>Nueva entrada
        </BtnPrimary>}
        <button className={"cw-ib"+(mobFiltros?" act":"")} aria-label={nFiltros>0?`Filtros, ${nFiltros} activo${nFiltros===1?"":"s"}`:"Filtros"}
          title="Filtros" aria-expanded={mobFiltros} onClick={()=>{setMobFiltros(v=>!v);setMobVista(false);}}>
          <TbIcono ico="filtros"/>
          {nFiltros>0&&<i className="cw-ib-n" aria-hidden="true">{nFiltros}</i>}
        </button>
        {onImport&&!soloLectura&&<button className="cw-ib" aria-label="Importar entradas" title="Importar entradas" onClick={onImport}><TbIcono ico="importar"/></button>}
        {onExportPdf&&<button className="cw-ib" aria-label="Exportar PDF" title="Exportar PDF" onClick={()=>onExportPdf(filtered, agTab)}><TbIcono ico="pdf" size={24}/></button>}
        <button className={"cw-ib"+(mobVista?" act":"")} aria-label={"Vista: "+(VISTAS_TB.find(v=>v[0]===agTab)?.[1]||"Registro")}
          title="Vista" aria-expanded={mobVista} onClick={()=>{setMobVista(v=>!v);setMobFiltros(false);}}>
          <TbIcono ico={agTab}/><TbIcono ico="chev" size={12}/>
        </button>
        {isAdminView&&<button className={"cw-ib"+(showAdminBanner||isViewingOther?" act":"")} aria-label="Cuaderno de otra persona"
          title="Cuaderno de otra persona" aria-expanded={showAdminBanner} onClick={()=>setShowAdminBanner(p=>!p)}>
          <TbIcono ico="otra"/>
          {isViewingOther&&<i className="cw-ib-n cw-ib-punto" aria-hidden="true"/>}
        </button>}
        {!soloLectura&&<button className={"cw-ib"+(showTypeMgr?" act":"")} aria-label="Gestionar tipos" title="Gestionar tipos"
          aria-expanded={showTypeMgr} onClick={()=>setShowTypeMgr(p=>!p)}><TbIcono ico="tipos"/></button>}
      </div>
      {mobVista&&(
        <div className="cw-mob-only cw-tb-panel" role="group" aria-label="Elegir vista">
          {VISTAS_TB.map(([v,lb])=>(
            <button key={v} className={"cw-vista-op"+(agTab===v?" on":"")} aria-pressed={agTab===v}
              onClick={()=>{setAgTab(v);setMobVista(false);}}>
              <TbIcono ico={v} size={18}/>{lb}{agTab===v&&<span style={{marginLeft:"auto"}} aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
      {isViewingOther&&!showAdminBanner&&(
        <div className="cw-mob-only cw-otro-aviso">{soloLectura?"Solo lectura: cuaderno de ":"Editando el cuaderno de "}{viewingUser?.name}</div>
      )}

      {/* Banner admin — selector de usuario (colapsable) */}
      {isAdminView&&showAdminBanner&&(
        <div className="filters-bar cw-bar" style={{background:"#0f172a",padding:"8px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0,borderBottom:"1px solid #1e3a4a"}}>
          <Icon name="eye" size={13} color="#87ceeb"/>
          <span style={{fontSize:11.5,color:"#87ceeb",fontWeight:600,flexShrink:0}}>Cuaderno de:</span>
          <select aria-label="Cuaderno de" value={viewTeamId||""} onChange={e=>{setViewTeamId(e.target.value||null);setViewUserId(null);}}
            style={{padding:"4px 9px",borderRadius:7,border:"1px solid #6b8cb3",background:"#1e293b",color:"#87ceeb",fontSize:11,fontFamily:"var(--font)",cursor:"pointer"}}>
            <option value="">Todos los equipos</option>
            {equiposVer.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {cuadernosVer.filter(u=>u.active!==false)
              .filter(u=>!viewTeamId||(teams||[]).find(t=>String(t.id)===String(viewTeamId))?.members?.includes(String(u.id)))
              .map(u=>(
                <button key={u.id} className="btn" onClick={()=>setViewUserId(String(u.id)===String(currentUser?.id)?null:u.id)}
                  style={{padding:"4px 11px",borderRadius:20,border:"1px solid",cursor:"pointer",fontSize:11,fontFamily:"var(--font)",fontWeight:600,transition:"all .15s",
                    borderColor:String(effectiveUserId)===String(u.id)?"#086C76":"#6b8cb3",
                    background:String(effectiveUserId)===String(u.id)?"#086C76":"transparent",
                    color:String(effectiveUserId)===String(u.id)?"#fff":"#8fb8c8"}}>
                  {shortName(u.name)}
                </button>
              ))}
          </div>
          {isViewingOther&&<span style={{marginLeft:"auto",fontSize:11,color:"#8fb8c8",fontStyle:"italic",flexShrink:0}}>{soloLectura?"Solo lectura":"Editando"}: {viewingUser?.name}</span>}
        </div>
      )}
      {soloLectura&&(
        <div role="status" className="cw-bar" style={{background:"var(--warn-bg)",color:"var(--warn-text)",borderBottom:"1px solid var(--warn-line)",padding:"7px 20px",fontSize:12,fontWeight:600,flexShrink:0}}>
          Estás viendo el cuaderno de {viewingUser?.name}, solo lectura.
        </div>
      )}

      {/* Sub-header fila 1 — acciones principales */}
      <div className="filters-bar cw-bar cw-desk-only" style={{background:"var(--surface)",borderBottom:"1px solid var(--border2)",padding:"7px 20px",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        {!soloLectura&&<BtnPrimary onClick={()=>addRow()}>
          <Icon name="plus-lg" size={13} color="#fff"/>Nueva entrada
        </BtnPrimary>}
        {onImport&&!soloLectura&&<button className="btn" aria-label="Importar entradas" title="Importar entradas" onClick={onImport}
          style={{padding:"6px 9px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",color:"var(--text2)",cursor:"pointer",display:"flex",alignItems:"center",gap:5,fontSize:11.5,fontFamily:"var(--font)"}}>
          <Icon name="upload" size={13} color="var(--text2)"/><span className="cw-lbl">Importar entradas</span>
        </button>}
        {onExportPdf&&<button className="btn" aria-label="Exportar PDF" title="Exportar PDF" onClick={()=>onExportPdf(filtered, agTab)}
          style={{padding:"6px 9px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",color:"var(--text2)",cursor:"pointer",display:"flex",alignItems:"center",gap:5,fontSize:11.5,fontFamily:"var(--font)"}}>
          <Icon name="file-text" size={13} color="var(--text2)"/><span className="cw-lbl">Exportar PDF</span>
        </button>}
        {!soloLectura&&<button className="btn" title="Cambiar nombres, colores e iconos de los tipos de entrada" onClick={()=>setShowTypeMgr(p=>!p)}
          style={{padding:"6px 9px",borderRadius:8,border:"1px solid var(--border)",background:showTypeMgr?"var(--accent-bg)":"var(--surface)",color:showTypeMgr?"var(--accent)":"var(--text2)",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:4}}>
          <Icon name="pencil-square" size={11} color={showTypeMgr?"var(--accent)":"var(--text3)"}/><span className="cw-lbl">Gestionar tipos</span>
        </button>}
        <div style={{display:"flex",background:"var(--bg)",borderRadius:8,padding:2,gap:1,border:"1px solid var(--border)"}}>
          {[["tabla","list-ul","Registro"],["crono","clock-history","Cronología"],["dash","bar-chart-fill","Resumen"]].map(([v,ic,lb])=>(
            <button key={v} className="btn" onClick={()=>setAgTab(v)}
              style={{padding:"5px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:4,
                background:agTab===v?"var(--surface)":"transparent",color:agTab===v?"var(--accent)":"var(--text3)",
                boxShadow:agTab===v?"var(--shadow-sm)":"none",transition:"all .15s"}}>
              <Icon name={ic} size={12} color={agTab===v?"var(--accent)":"var(--text3)"}/>{lb}
            </button>
          ))}
        </div>
        {isAdminView&&(
          <button className="btn" onClick={()=>setShowAdminBanner(p=>!p)}
            style={{padding:"5px 9px",borderRadius:8,border:"1px solid "+(showAdminBanner?"#6b8cb3":"var(--border)"),background:showAdminBanner?"#0f172a":"var(--surface)",color:showAdminBanner?"#87ceeb":"var(--text2)",cursor:"pointer",display:"flex",alignItems:"center",gap:5,fontSize:11,fontFamily:"var(--font)"}}>
            <Icon name="people" size={12} color={showAdminBanner?"#87ceeb":"var(--text2)"}/>Cuaderno de otra persona
          </button>
        )}
        <span data-bar-end style={{marginLeft:"auto",fontSize:11,color:"var(--text3)",whiteSpace:"nowrap"}}>{filtered.length} {filtered.length===1?"entrada":"entradas"} · <b style={{color:"var(--accent)"}}>{fmtHours(totalH)}</b></span>
      </div>

      {/* Sub-header fila 2 — filtros */}
      <div className={"filters-bar cw-bar cw-bar-filtros"+(mobFiltros?" abierta":"")} style={{background:"var(--surface2)",borderBottom:"1px solid var(--border)",padding:"6px 20px",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        {/* UN control para las fechas: periodo, mes y rango ya no son cuatro piezas sueltas */}
        <FiltroFechas per={filterPer} mes={filterMes} desde={filterDesde} hasta={filterHasta} meses={allMonths}
          onChange={({per,mes,desde,hasta})=>{setFilterPer(per);setFilterMes(mes);setFilterDesde(desde);setFilterHasta(hasta);}}/>
        <select aria-label="Tipo" value={filterTipo} onChange={e=>setFilterTipo(e.target.value)} style={selS}>
          <option value="">Todos los tipos</option>
          {activeTipos.map(t=><option key={t}>{t}</option>)}
          {archivedTipos.filter(t=>myEntries.some(a=>a.type===t)).map(t=>(
            <option key={t} value={t}>{t} (archivado)</option>
          ))}
        </select>
        <select aria-label="Equipo" value={filterDpto} onChange={e=>setFilterDpto(e.target.value)} style={selS}>
          <option value="">Todos los equipos</option>
          {(teams||[]).map(t=><option key={t.id}>{t.name}</option>)}
        </select>
        {/* Proyecto de Notion: un filtro más de la barra. Antes era una fila de
            pestañas propia, que se desbordaba en cuanto había varios proyectos. */}
        {(notionTabs.length>1||filterNotion)&&(
          <select value={filterNotion} onChange={e=>setFilterNotion(e.target.value)}
            style={{...selS,maxWidth:200}}
            title={notionTabs.find(t=>t.item.external_id===filterNotion)?.item.title||"Todos los proyectos"}>
            <option value="">Todos los proyectos</option>
            {notionTabs.map(t=>(
              <option key={t.item.external_id} value={t.item.external_id}>
                {t.item.icon ? t.item.icon+" " : ""}{t.item.title} ({t.count} · {fmtHours(t.hours)})
              </option>
            ))}
          </select>
        )}
        <div style={{position:"relative",flex:"1 1 140px",minWidth:140,maxWidth:180}}>
          <input aria-label="Buscar en el cuaderno" value={filterQ} onChange={e=>setFilterQ(e.target.value)} placeholder="Buscar…"
            style={{width:"100%",padding:"4px 8px 4px 26px",borderRadius:7,border:"1px solid var(--border)",fontSize:11,fontFamily:"var(--font)",background:"var(--surface)",color:"var(--text)"}}/>
          <Icon name="search" size={10} color="var(--text3)" style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)"}}/>
        </div>
      </div>

      <div className="cw-mob-only cw-cnt-mob">{filtered.length} {filtered.length===1?"entrada":"entradas"} · <b style={{color:"var(--accent)"}}>{fmtHours(totalH)}</b></div>

      {/* Gestor de tipos */}
      {showTypeMgr&&!soloLectura&&(
        <div style={{background:"var(--warn-bg)",borderBottom:"1px solid var(--warn-line)",padding:"12px 20px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:9,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:"var(--warn-text)",display:"flex",alignItems:"center",gap:5}}>
              <Icon name="pencil-square" size={12} color="var(--warn-text)"/>Tipos de entrada
            </span>
            {agendaTipos.map(tp=>{
              const row = tipoRow(tp);
              const use = tiposUsage[tp] || {count:0,hours:0};
              return (
                <TypePill key={tp} name={tp}
                  color={getTipoColor(tp,agendaTipos,agendaTiposData)}
                  icon={getTipoIcon(tp,agendaTiposData)}
                  count={use.count} hours={use.hours}
                  archived={!!row?.archived}
                  onEdit={()=>setTipoModal({tipo:row||{name:tp}})}/>
              );
            })}
            <button className="btn" onClick={()=>setTipoModal({tipo:null})}
              style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 11px",borderRadius:20,border:"1px dashed #d97706",background:"var(--surface)",color:"var(--warn-text)",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"var(--font)"}}>
              <Icon name="plus-lg" size={10} color="var(--warn-text)"/>Nuevo tipo
            </button>
            <span style={{fontSize:11,color:"var(--warn-text)",opacity:.75,marginLeft:"auto"}}>
              Clic en un tipo para editar nombre, color e icono · arrastra las cabeceras de grupo para reordenar
            </span>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <div style={{flex:1,overflow:"auto",padding:16}}>
        {agTab==="tabla" ? (
          <div ref={newRef}>
            {[...activeTipos.map((tp,i)=>({tp,tpIdx:i,archivedGrp:false})),
              ...archivedTipos.filter(tp=>filtered.some(a=>a.type===tp)).map(tp=>({tp,tpIdx:null,archivedGrp:true}))
             ].map(({tp,tpIdx,archivedGrp})=>{
              const grp=filtered.filter(a=>a.type===tp);
              const isOpen=!collapsed.has(tp);
              const col=getTipoColor(tp,agendaTipos,agendaTiposData);
              const tpIcon=getTipoIcon(tp,agendaTiposData);
              const grpH=grp.reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
              // Un tipo archivado no admite reordenación ni recibe entradas nuevas
              const isDragEntryTarget=!archivedGrp&&dragOverTipo===tp&&dragEntryId;
              const isGrpOver=!archivedGrp&&dragOverGrpIdx===tpIdx&&dragGrpIdx!==null&&dragGrpIdx!==tpIdx;
              if(!grp.length&&!isDragEntryTarget) return null;
              return (
                <div key={tp}
                  style={{marginBottom:12,opacity:dragGrpIdx===tpIdx?.45:1,
                    borderTop:isGrpOver?"3px solid "+col:"3px solid transparent",borderRadius:2,transition:"all .13s"}}
                  onDragOver={archivedGrp?undefined:e=>{onDragOverTipo(tp,e);onGrpDragOver(tpIdx,e);}}
                  onDragLeave={archivedGrp?undefined:()=>{setDragOverTipo(null);setDragOverGrpIdx(null);}}
                  onDrop={archivedGrp?undefined:()=>{onDropOnTipo(tp);onGrpDrop(tpIdx);}}>

                  {/* Cabecera del grupo */}
                  <div draggable={!archivedGrp&&!soloLectura}
                    onDragStart={archivedGrp?undefined:e=>onGrpDragStart(tpIdx,e)}
                    onDragEnd={()=>{setDragGrpIdx(null);setDragOverGrpIdx(null);}}
                    {...clicable(()=>setCollapsed(prev=>{const n=new Set(prev);n.has(tp)?n.delete(tp):n.add(tp);return n;}),{expanded:isOpen})}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderRadius:9,background:colorConBlanco(col),boxShadow:"0 2px 8px "+colorConBlanco(col)+"55",cursor:archivedGrp?"pointer":"grab",userSelect:"none",marginBottom:isOpen?6:0,opacity:archivedGrp?.72:1}}>
                    {!archivedGrp&&(
                      <div style={{display:"flex",flexDirection:"column",gap:2.5,flexShrink:0,opacity:.6}}>
                        {[0,1,2].map(i=><span key={i} style={{width:14,height:2,background:"rgba(255,255,255,.9)",borderRadius:1,display:"block"}}/>)}
                      </div>
                    )}
                    {tpIcon&&<Icon name={tpIcon} size={13} color="#fff" style={{flexShrink:0}}/>}
                    <span style={{fontSize:12.5,fontWeight:700,color:"#fff",flex:1}}>
                      {tp}
                      {archivedGrp&&<span style={{fontWeight:600,fontSize:11,letterSpacing:".05em",marginLeft:7,padding:"2px 7px",borderRadius:20,background:"rgba(0,0,0,.25)"}}>ARCHIVADO</span>}
                    </span>
                    <span style={{fontSize:11,background:"rgba(0,0,0,.2)",color:"#fff",padding:"2px 8px",borderRadius:20,fontWeight:600}}>{grp.length}</span>
                    <span style={{fontSize:15,fontWeight:800,color:"#fff",minWidth:42,textAlign:"right"}}>{fmtHours(grpH)}</span>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" style={{flexShrink:0,transition:"transform .2s",transform:isOpen?"rotate(0deg)":"rotate(-90deg)"}}>
                      <path d="M2 5L8 11L14 5" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>

                  {/* Entradas del grupo */}
                  {isOpen&&grp.map(a=>(
                    <EntryRow key={a.id} a={a} col={col}
                      isEdit={String(editId)===String(a.id)} festivos={festivos}
                      draggable={!archivedGrp&&!soloLectura} dragging={String(dragEntryId)===String(a.id)}
                      activeTipos={activeTipos} archivedTipos={archivedTipos}
                      teams={teams} allUsers={allUsers} notionItems={notionItems} misPasos={misPasos}
                      editTimes={editTimes} setEditTimes={setEditTimes}
                      cronoEstado={cronoDe(a.id)} onAbrirIncidencia={onAbrirIncidencia}
                      canVerIncidencias={canVerIncidencias} origenTipo={origenDe(a.type)}
                      onOpen={soloLectura?undefined:()=>openEntry(a)} soloLectura={soloLectura} onCloseEdit={()=>{flushPending(a.id);setEditId(null);}}
                      onDragStartEntry={()=>onDragStart(a.id)}
                      onDragEndEntry={()=>{setDragEntryId(null);setDragOverTipo(null);}}
                      updateEntry={updateEntry} rellenarDestino={rellenarDestino} duplicateEntry={duplicateEntry}
                      deleteEntry={deleteEntry} autoCalcHours={autoCalcHours}/>
                  ))}

                  {grp.length===0&&isDragEntryTarget&&(
                    <div style={{padding:"14px",textAlign:"center",border:`2px dashed ${col}`,borderRadius:9,color:col,fontSize:11,fontWeight:600,background:col+"08"}}>
                      Suelta aquí para mover a "{tp}"
                    </div>
                  )}
                </div>
              );
            })}

            {/* Grupo "Otros" para entradas con tipo null o tipo eliminado */}
            {(()=>{
              const otrosGrp=filtered.filter(a=>!a.type||!groupTipos.includes(a.type));
              if(!otrosGrp.length) return null;
              const col="#6b7280";
              const isOpen=!collapsed.has("__otros__");
              const grpH=otrosGrp.reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
              return (
                <div style={{marginBottom:12}}>
                  <div {...clicable(()=>setCollapsed(prev=>{const n=new Set(prev);n.has("__otros__")?n.delete("__otros__"):n.add("__otros__");return n;}),{expanded:isOpen})}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderRadius:9,background:col,boxShadow:"0 2px 8px "+col+"55",cursor:"pointer",userSelect:"none",marginBottom:isOpen?6:0}}>
                    <span style={{fontSize:12.5,fontWeight:700,color:"#fff",flex:1,display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                      Otros
                      <span style={{fontWeight:400,fontSize:11,opacity:.8}}>sin tipo — estas horas no entran en la distribución por tipo; abre cada entrada y asígnale uno</span>
                    </span>
                    <span style={{fontSize:11,background:"rgba(0,0,0,.2)",color:"#fff",padding:"2px 8px",borderRadius:20,fontWeight:600}}>{otrosGrp.length}</span>
                    <span style={{fontSize:15,fontWeight:800,color:"#fff",minWidth:42,textAlign:"right"}}>{fmtHours(grpH)}</span>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" style={{flexShrink:0,transition:"transform .2s",transform:isOpen?"rotate(0deg)":"rotate(-90deg)"}}>
                      <path d="M2 5L8 11L14 5" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {isOpen&&otrosGrp.map(a=>(
                    <EntryRow key={a.id} a={a} col={col}
                      isEdit={String(editId)===String(a.id)} festivos={festivos}
                      draggable={!soloLectura} dragging={String(dragEntryId)===String(a.id)}
                      activeTipos={activeTipos} archivedTipos={archivedTipos}
                      teams={teams} allUsers={allUsers} notionItems={notionItems} misPasos={misPasos}
                      editTimes={editTimes} setEditTimes={setEditTimes}
                      cronoEstado={cronoDe(a.id)} onAbrirIncidencia={onAbrirIncidencia}
                      canVerIncidencias={canVerIncidencias} origenTipo={origenDe(a.type)}
                      onOpen={soloLectura?undefined:()=>openEntry(a)} soloLectura={soloLectura} onCloseEdit={()=>{flushPending(a.id);setEditId(null);}}
                      onDragStartEntry={()=>onDragStart(a.id)}
                      onDragEndEntry={()=>{setDragEntryId(null);setDragOverTipo(null);}}
                      updateEntry={updateEntry} rellenarDestino={rellenarDestino} duplicateEntry={duplicateEntry}
                      deleteEntry={deleteEntry} autoCalcHours={autoCalcHours}/>
                  ))}
                </div>
              );
            })()}

            {filtered.length===0&&!dragEntryId&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"var(--text3)"}}>
                <Icon name="journal-text" size={32} color="#d1d5db" style={{display:"block",margin:"0 auto 12px"}}/>
                <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Sin entradas para este filtro</div>
                <div style={{fontSize:12,marginBottom:16}}>{soloLectura?"Cambia el período":"Cambia el período o crea una nueva entrada"}</div>
                {!soloLectura&&<BtnPrimary onClick={()=>addRow()}>
                  <Icon name="plus-lg" size={13} color="#fff"/>Nueva entrada
                </BtnPrimary>}
              </div>
            )}
          </div>
        ) : agTab==="crono" ? (
          /* Cronologia: el dia por dentro, para ver que tareas se pisan */
          <CronologiaDiaView
            entradas={filtered}
            agendaTipos={agendaTipos}
            agendaTiposData={agendaTiposData}
            festivos={festivos}
            editId={editId}
            onOpen={a=>{openEntry(a);setAgTab("tabla");}}
          />
        ) : (
          /* Dashboard */
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* KPIs */}
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {kpis.map(([ic,cl,val,lab,sub],i)=>(
                <div key={lab} style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:"13px 16px",flex:"1 1 120px",borderTop:`4px solid ${cl}`,boxShadow:"var(--shadow-sm)"}}>
                  <Icon name={ic} size={17} color={cl} style={{marginBottom:5,display:"block"}}/>
                  <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",color:"var(--text3)",marginBottom:3}}>{lab}</div>
                  <div style={{fontSize:22,fontWeight:800,color:cl,lineHeight:1}}>{val}</div>
                  <div style={{fontSize:11,fontWeight:600,color:cl,marginTop:3}}>{sub}</div>
                </div>
              ))}
            </div>

            {/* Horas por proyecto: los de Notion y los del gestor de proyectos */}
            {(()=>{
              // Los pasos del gestor traen consigo su proyecto, asi que misPasos
              // sirve de indice paso->proyecto y proyecto->nombre/color/icono.
              // getMisPasos NO devuelve los pasos cerrados ni los de proyectos
              // archivados: si el paso imputado falta, el proyecto se recupera
              // igual cuando algun otro paso suyo si esta en la lista.
              const pasoById=new Map(misPasos.map(s=>[String(s.id),s]));
              const projDeGestor=new Map();
              misPasos.forEach(s=>{
                const pid=s.project_id&&String(s.project_id);
                if(pid&&!projDeGestor.has(pid))
                  projDeGestor.set(pid,{title:s.project_name,color:s.project_color,icon:s.project_icon});
              });

              const projMap=new Map();
              const grupo=(key,data)=>{
                if(!projMap.has(key)) projMap.set(key,{key,...data,hours:0,count:0,tasks:new Map()});
                return projMap.get(key);
              };
              const sumar=(entry,a,task)=>{
                const h=parseFloat(a.hours)||0;
                entry.hours+=h; entry.count+=1;
                if(!entry.tasks.has(task.id)) entry.tasks.set(task.id,{...task,hours:0,count:0});
                const t=entry.tasks.get(task.id);
                t.hours+=h; t.count+=1;
              };

              filtered.forEach(a=>{
                const proj=a.external_project_id&&notionItems.find(i=>i.external_id===a.external_project_id);
                if(proj){
                  const entry=grupo(`notion:${a.external_project_id}`,
                    {origen:"Notion",title:proj.title,icon:proj.icon,iconTipo:"emoji",color:"#0ea5e9"});
                  const taskItem=a.external_task_id&&notionItems.find(i=>i.external_id===a.external_task_id);
                  sumar(entry,a,{
                    id:a.external_task_id||`__desc__${a.description||"—"}`,
                    title:taskItem?.title||a.description||"Sin tarea específica",
                    icon:taskItem?.icon||null,
                    vinculada:!!taskItem,
                  });
                  return;
                }
                // Entradas imputadas a un paso del gestor: el proyecto es el del
                // paso y el desglose de "tareas" son los pasos trabajados.
                const paso=a.task_id?pasoById.get(String(a.task_id)):null;
                const pid=(a.project_id&&String(a.project_id))||(paso&&String(paso.project_id))||null;
                if(!pid) return;
                const info=projDeGestor.get(pid)||{};
                const entry=grupo(`gestor:${pid}`,{
                  origen:"Gestor",
                  title:info.title||`Proyecto #${pid}`,
                  icon:info.icon||"diagram-3",
                  iconTipo:"bi",
                  color:info.color||"#8b5cf6",
                });
                sumar(entry,a,{
                  id:a.task_id?`paso:${a.task_id}`:`__desc__${a.description||"—"}`,
                  title:paso?.title||a.description||"Sin paso específico",
                  icon:null,
                  vinculada:!!paso,
                });
              });
              const projects=[...projMap.values()]
                .map(p=>({...p,taskList:[...p.tasks.values()].sort((a,b)=>b.hours-a.hours)}))
                .sort((a,b)=>b.hours-a.hours);
              if(!projects.length) return null;
              const maxH=Math.max(...projects.map(p=>p.hours),1);
              return (
                <div style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:16,boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontWeight:700,fontSize:11,color:"var(--text2)",marginBottom:12,textTransform:"uppercase",letterSpacing:".07em",display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="link-45deg" size={13} color="var(--accent)"/>Horas por proyecto
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    {projects.map(p=>{
                      const maxTaskH=Math.max(...p.taskList.map(t=>t.hours),1);
                      const projColor=p.color;
                      return (
                      <div key={p.key}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4,alignItems:"center",gap:8}}>
                          <span style={{fontWeight:600,color:"var(--text2)",display:"flex",alignItems:"center",gap:5,overflow:"hidden"}}>
                            {p.icon&&(p.iconTipo==="bi"
                              ? <Icon name={p.icon} size={12} color={projColor} style={{flexShrink:0}}/>
                              : <span style={{flexShrink:0,fontSize:13}}>{p.icon}</span>)}
                            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.title}</span>
                            {/* De donde sale el proyecto: los dos origenes conviven en la misma lista */}
                            <span style={{flexShrink:0,fontSize:11,fontWeight:700,padding:"1px 6px",borderRadius:20,background:projColor+"18",color:projColor,textTransform:"uppercase",letterSpacing:".04em"}}>{p.origen}</span>
                          </span>
                          <span style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                            <span style={{fontSize:11,color:"var(--text3)",fontWeight:500}}>{p.count} entrada{p.count!==1?"s":""}</span>
                            <span style={{fontWeight:700,color:projColor}}>{p.hours.toFixed(1)}h</span>
                          </span>
                        </div>
                        <div style={{height:6,background:"var(--bg)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.round(p.hours/maxH*100)}%`,background:projColor,borderRadius:3,transition:"width .6s"}}/>
                        </div>
                        {/* Desglose por tarea de Notion o por paso del gestor */}
                        {p.taskList.length>0&&(
                          <div style={{display:"flex",flexDirection:"column",gap:5,marginTop:7,paddingLeft:14,borderLeft:`2px solid ${projColor}33`}}>
                            {p.taskList.map(t=>(
                              <div key={t.id}>
                                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3,alignItems:"center",gap:8}}>
                                  <span style={{color:"var(--text2)",display:"flex",alignItems:"center",gap:5,overflow:"hidden"}}>
                                    {t.icon
                                      ? <span style={{flexShrink:0,fontSize:11}}>{t.icon}</span>
                                      : t.vinculada
                                        ? <Icon name={p.origen==="Notion"?"file-text":"diagram-2"} size={10} color="var(--text3)" style={{flexShrink:0}}/>
                                        : <span style={{flexShrink:0,width:4,height:4,borderRadius:"50%",background:"var(--text3)",display:"inline-block"}}/>}
                                    <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</span>
                                  </span>
                                  <span style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                    <span style={{fontSize:11,color:"var(--text3)"}}>{t.count}×</span>
                                    <span style={{fontWeight:700,color:"var(--text2)"}}>{t.hours.toFixed(1)}h</span>
                                  </span>
                                </div>
                                <div style={{height:4,background:"var(--bg)",borderRadius:2,overflow:"hidden"}}>
                                  <div style={{height:"100%",width:`${Math.round(t.hours/maxTaskH*100)}%`,background:projColor,opacity:.55,borderRadius:2,transition:"width .6s"}}/>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Reparto del tiempo por productividad */}
            {prod.index!==null&&(()=>{
              const bandas=[
                {...prodBand(100), horas:prod.alta },
                {...prodBand(50),  horas:prod.media},
                {...prodBand(0),   horas:prod.baja },
              ].filter(b=>b.horas>0);
              // El denominador del reparto son las horas CON tipo: las de "Otros"
              // no se pueden ponderar y se muestran aparte para no falsear el %.
              const base=prod.horas||1;
              const maxB=Math.max(...bandas.map(b=>b.horas),1);
              return (
                <div style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:16,boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontWeight:700,fontSize:11,color:"var(--text2)",marginBottom:12,textTransform:"uppercase",letterSpacing:".07em",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <Icon name="graph-up-arrow" size={13} color="var(--accent)"/>Productividad del tiempo dedicado
                    <span style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:7,textTransform:"none",letterSpacing:0}}>
                      <span style={{fontSize:19,fontWeight:800,color:prodBandActual.color,lineHeight:1}}>{prod.index}%</span>
                      <span style={{fontSize:11,fontWeight:700,padding:"2px 9px",borderRadius:20,background:prodBandActual.color+"18",color:prodBandActual.color}}>
                        {prodBandActual.label}
                      </span>
                    </span>
                  </div>

                  {/* Una sola barra: de un golpe se ve dónde está el tiempo */}
                  <div style={{height:14,borderRadius:7,overflow:"hidden",display:"flex",background:"var(--bg)",marginBottom:9}}>
                    {bandas.map(b=>(
                      <div key={b.key} title={`${b.label}: ${fmtHours(b.horas)} (${Math.round(b.horas/base*100)}%)`}
                        style={{height:"100%",width:`${Math.round(b.horas/base*100)}%`,background:b.color,transition:"width .6s"}}/>
                    ))}
                  </div>

                  <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:11}}>
                    {bandas.map(b=>(
                      <div key={b.key}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4,gap:8}}>
                          <span style={{fontWeight:600,color:"var(--text2)",display:"flex",alignItems:"center",gap:5}}>
                            <span style={{width:8,height:8,borderRadius:2,background:b.color,display:"inline-block",flexShrink:0}}/>
                            Productividad {b.label.toLowerCase()}
                            <span style={{color:"var(--text3)",fontWeight:500}}>
                              {b.key==="alta"?"peso ≥70":b.key==="media"?"peso 40-69":"peso <40"}
                            </span>
                          </span>
                          <span style={{fontWeight:700,color:b.color,whiteSpace:"nowrap",flexShrink:0}}>
                            {fmtHours(b.horas)} <span style={{fontWeight:600,opacity:.8}}>· {Math.round(b.horas/base*100)}%</span>
                          </span>
                        </div>
                        <div style={{height:6,background:"var(--bg)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.round(b.horas/maxB*100)}%`,background:b.color,borderRadius:3,transition:"width .6s"}}/>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Relación de dedicación: el dato que pide contexto para el % */}
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",borderRadius:9,background:"var(--bg)",fontSize:11.5,color:"var(--text2)",lineHeight:1.45,flexWrap:"wrap"}}>
                    <Icon name="bar-chart-fill" size={12} color="var(--text3)"/>
                    {prod.baja>0
                      ? <span><b style={{color:prod.ratio>=1?"var(--ok-text)":"var(--red-text)"}}>{fmtRatio(prod.ratio)}</b> — {fmtHours(prod.alta)} de alta productividad por cada {fmtHours(prod.baja)} de baja</span>
                      : <span>{fmtHours(prod.alta)} de alta productividad y <b>ni una hora</b> de baja</span>}
                  </div>

                  {prod.baja>prod.alta&&(
                    <div style={{marginTop:8,padding:"9px 12px",borderRadius:9,background:"var(--red-bg)",border:"1px solid var(--red-line)",fontSize:11.5,color:"var(--red-text)",display:"flex",alignItems:"flex-start",gap:6,lineHeight:1.45}}>
                      <Icon name="exclamation-triangle-fill" size={12} color="#b91c1c" style={{marginTop:1,flexShrink:0}}/>
                      <span>En este período hay más tiempo en tipos de baja productividad ({fmtHours(prod.baja)}) que en los de alta ({fmtHours(prod.alta)}).</span>
                    </div>
                  )}

                  {prod.sinTipo>0&&(
                    <div style={{marginTop:8,padding:"9px 12px",borderRadius:9,background:"var(--bg)",border:"1px dashed var(--border)",fontSize:11,color:"var(--text3)",display:"flex",alignItems:"flex-start",gap:6,lineHeight:1.45}}>
                      <Icon name="inbox" size={12} color="var(--text3)" style={{marginTop:1,flexShrink:0}}/>
                      <span>{fmtHours(prod.sinTipo)} sin tipo quedan fuera del índice — no se les puede asignar un peso. Asígnales uno desde el grupo «Otros» del registro.</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Fila: distribución por tipo + horas por equipo */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:14}}>
              {/* Distribución por tipo */}
              <div style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:16,boxShadow:"var(--shadow-sm)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"var(--text2)",marginBottom:12,textTransform:"uppercase",letterSpacing:".07em",display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="bar-chart-fill" size={13} color="var(--accent)"/>Distribución por tipo
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {agendaTipos.filter(tp=>totalByTipo[tp]>0).map(tp=>{
                    const maxH=Math.max(...Object.values(totalByTipo).filter(v=>v>0),1);
                    const col=getTipoColor(tp,agendaTipos,agendaTiposData);
                    // El peso al lado de las horas: sin él no se distingue una
                    // barra larga que aporta de una barra larga que resta.
                    const pw=getTipoProd(tp,agendaTiposData);
                    const pb=prodBand(pw);
                    return (
                      <div key={tp}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4,gap:8}}>
                          <span style={{fontWeight:600,color:"var(--text2)",display:"flex",alignItems:"center",gap:6,overflow:"hidden"}}>
                            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tp}</span>
                            <span title={`Productividad ${pb.label.toLowerCase()} — peso ${pw}%`}
                              style={{flexShrink:0,fontSize:11,fontWeight:700,padding:"1px 6px",borderRadius:20,background:pb.color+"18",color:pb.color}}>
                              {pw}%
                            </span>
                          </span>
                          <span style={{fontWeight:700,color:col,whiteSpace:"nowrap",flexShrink:0}}>{totalByTipo[tp].toFixed(1)}h</span>
                        </div>
                        <div style={{height:6,background:"var(--bg)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.round(totalByTipo[tp]/maxH*100)}%`,background:col,borderRadius:3,transition:"width .6s"}}/>
                        </div>
                      </div>
                    );
                  })}
                  {agendaTipos.filter(tp=>totalByTipo[tp]>0).length===0&&(
                    <div style={{textAlign:"center",fontSize:12,color:"var(--text3)",padding:"12px 0"}}>Sin datos para el período</div>
                  )}
                </div>
              </div>

              {/* Horas por equipo */}
              {(()=>{
                const TEAM_CLR=["#6366f1","#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6"];
                const teamsData=(teams||[]).map((t,ti)=>({
                  ...t,
                  color:TEAM_CLR[ti%TEAM_CLR.length],
                  hours:filtered.filter(a=>a.department===t.name).reduce((s,a)=>s+(parseFloat(a.hours)||0),0)
                })).filter(t=>t.hours>0).sort((a,b)=>b.hours-a.hours);
                const maxH=Math.max(...teamsData.map(t=>t.hours),1);
                return (
                  <div style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:16,boxShadow:"var(--shadow-sm)"}}>
                    <div style={{fontWeight:700,fontSize:11,color:"var(--text2)",marginBottom:12,textTransform:"uppercase",letterSpacing:".07em",display:"flex",alignItems:"center",gap:6}}>
                      <Icon name="people" size={13} color="var(--accent)"/>Horas por equipo
                    </div>
                    {teamsData.length>0?(
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {teamsData.map(t=>(
                          <div key={t.id}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                              <span style={{fontWeight:600,color:"var(--text2)"}}>{t.name}</span>
                              <span style={{fontWeight:700,color:t.color}}>{t.hours.toFixed(1)}h</span>
                            </div>
                            <div style={{height:6,background:"var(--bg)",borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${Math.round(t.hours/maxH*100)}%`,background:t.color,borderRadius:3,transition:"width .6s"}}/>
                            </div>
                          </div>
                        ))}
                      </div>
                    ):(
                      <div style={{textAlign:"center",fontSize:12,color:"var(--text3)",padding:"12px 0"}}>Sin datos para el período</div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Tipos desglosados por equipo — barras apiladas */}
            {(()=>{
              const TEAM_CLR=["#6366f1","#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6"];
              const teamsData=(teams||[]).map((t,ti)=>{
                const tEntries=filtered.filter(a=>a.department===t.name);
                const totalH=tEntries.reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
                const byTipo=agendaTipos.map(tp=>({
                  tipo:tp,
                  hours:tEntries.filter(a=>a.type===tp).reduce((s,a)=>s+(parseFloat(a.hours)||0),0),
                  color:getTipoColor(tp,agendaTipos,agendaTiposData)
                })).filter(x=>x.hours>0);
                return {...t,teamColor:TEAM_CLR[ti%TEAM_CLR.length],totalH,byTipo};
              }).filter(t=>t.totalH>0).sort((a,b)=>b.totalH-a.totalH);
              if(!teamsData.length) return null;
              return (
                <div style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:16,boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontWeight:700,fontSize:11,color:"var(--text2)",marginBottom:12,textTransform:"uppercase",letterSpacing:".07em",display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="bar-chart-fill" size={13} color="var(--accent)"/>Tipos por equipo
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    {teamsData.map(t=>(
                      <div key={t.id}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,marginBottom:5}}>
                          <span style={{fontWeight:700,color:t.teamColor,display:"flex",alignItems:"center",gap:5}}>
                            <span style={{width:8,height:8,borderRadius:"50%",background:t.teamColor,display:"inline-block",flexShrink:0}}/>
                            {t.name}
                          </span>
                          <span style={{fontWeight:700,color:"var(--text2)"}}>{t.totalH.toFixed(1)}h</span>
                        </div>
                        <div style={{height:10,borderRadius:5,overflow:"hidden",display:"flex",background:"var(--bg)"}}>
                          {t.byTipo.map(({tipo,hours,color})=>(
                            <div key={tipo} title={`${tipo}: ${hours.toFixed(1)}h`}
                              style={{height:"100%",width:`${Math.round(hours/t.totalH*100)}%`,background:color,transition:"width .6s"}}/>
                          ))}
                        </div>
                        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:5}}>
                          {t.byTipo.map(({tipo,hours,color})=>(
                            <span key={tipo} style={{display:"flex",alignItems:"center",gap:3,fontSize:11}}>
                              <span style={{width:6,height:6,borderRadius:2,background:color,display:"inline-block",flexShrink:0}}/>
                              <span style={{color:"var(--text2)",fontWeight:500}}>{tipo}</span>
                              <span style={{color:color,fontWeight:700}}>{hours.toFixed(1)}h</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Productividad por persona — solo admin */}
            {isManagerRole&&prodEquipo&&prodEquipo.length>0&&(()=>{
              const equipoSel=(teams||[]).find(t=>String(t.id)===String(viewTeamId));
              const miembros=equipoSel?.members?.map(String)||null;
              const filas=prodEquipo
                .filter(r=>r.indice!==null)
                .filter(r=>!miembros||miembros.includes(String(r.user_id)))
                .map(r=>{
                  const u=(allUsers||[]).find(x=>String(x.id)===String(r.user_id));
                  return {...r,nombre:u?.name||`Usuario ${r.user_id}`,activo:u?.active!==false};
                })
                .filter(r=>r.activo)
                .sort((a,b)=>b.indice-a.indice);
              if(!filas.length) return null;
              return (
                <div style={{background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:16,boxShadow:"var(--shadow-sm)"}}>
                  <div style={{fontWeight:700,fontSize:11,color:"var(--text2)",marginBottom:5,textTransform:"uppercase",letterSpacing:".07em",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <Icon name="people-fill" size={13} color="var(--accent)"/>
                    Productividad por persona{equipoSel?` · ${equipoSel.name}`:""}
                  </div>
                  {/* La comparativa se calcula en el backend por rango de fechas:
                      no aplica los filtros de tipo, equipo ni búsqueda. */}
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:12,lineHeight:1.45}}>
                    Del {fmt(rangoFiltrado?.desde)} al {fmt(rangoFiltrado?.hasta)} · solo por fechas: los filtros de tipo, equipo y búsqueda no se aplican aquí
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:11}}>
                    {filas.map(r=>{
                      const pb=prodBand(r.indice);
                      const base=r.horas||1;
                      return (
                        <div key={r.user_id}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,marginBottom:4,gap:8}}>
                            <span style={{fontWeight:600,color:"var(--text2)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {shortName(r.nombre)}
                            </span>
                            <span style={{display:"flex",alignItems:"center",gap:7,flexShrink:0,whiteSpace:"nowrap"}}>
                              <span style={{fontSize:11,color:"var(--text3)"}}>{fmtHours(r.horas)}</span>
                              <span style={{fontWeight:800,color:pb.color,fontSize:13}}>{r.indice}%</span>
                            </span>
                          </div>
                          <div style={{height:9,borderRadius:5,overflow:"hidden",display:"flex",background:"var(--bg)"}}>
                            {[["alta",r.horas_alta],["media",r.horas_media],["baja",r.horas_baja]].map(([k,h])=>{
                              if(!h) return null;
                              const c=AG_PROD_BANDS.find(b=>b.key===k).color;
                              return <div key={k} title={`Productividad ${k}: ${fmtHours(h)}`}
                                style={{height:"100%",width:`${Math.round(h/base*100)}%`,background:c,transition:"width .6s"}}/>;
                            })}
                          </div>
                          {r.horas_sin_tipo>0&&(
                            <div style={{fontSize:11,color:"var(--text3)",marginTop:3}}>
                              {fmtHours(r.horas_sin_tipo)} sin tipo, fuera del índice
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:12,paddingTop:10,borderTop:"1px solid var(--border)"}}>
                    {AG_PROD_BANDS.map(b=>(
                      <span key={b.key} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--text3)"}}>
                        <span style={{width:7,height:7,borderRadius:2,background:b.color,display:"inline-block"}}/>
                        Productividad {b.label.toLowerCase()}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

          </div>
        )}
      </div>

      {/* Footer en movil (<=600px): una sola linea con punto de color, icono y horas por
          tipo, y el total fijo a la derecha. Tocarla despliega el detalle. */}
      <div className="cw-mob-only" style={{background:"var(--surface2)",borderTop:"1px solid var(--border)",flexShrink:0}}>
        {verDetallePie&&(
          <div style={{padding:"4px 12px 8px",borderBottom:"1px solid var(--border2)",fontSize:12}}>
            {groupTipos.filter(tp=>totalByTipo[tp]>0).map(tp=>{
              const c=getTipoColor(tp,agendaTipos,agendaTiposData);
              const ico=getTipoIcon(tp,agendaTiposData);
              return (
                <div key={tp} style={{display:"flex",alignItems:"center",gap:7,padding:"4px 0",color:"var(--text2)"}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:c,display:"inline-block",flexShrink:0}}/>
                  {ico&&<Icon name={ico} size={12} color={c}/>}
                  <span>{tp}</span>
                  <span style={{fontSize:11,color:"var(--text3)"}}>{filtered.filter(a=>a.type===tp).length} entradas</span>
                  <b style={{marginLeft:"auto",color:"var(--text)"}}>{totalByTipo[tp].toFixed(1)}h</b>
                </div>
              );
            })}
          </div>
        )}
        <button className="btn" aria-expanded={verDetallePie} aria-label="Horas por tipo. Pulsa para ver el detalle"
          onClick={()=>setVerDetallePie(v=>!v)}
          style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",width:"100%",background:"none",border:"none",cursor:"pointer",fontFamily:"var(--font)",fontSize:12,textAlign:"left",overflow:"hidden"}}>
          {groupTipos.filter(tp=>totalByTipo[tp]>0).map(tp=>{
            const c=getTipoColor(tp,agendaTipos,agendaTiposData);
            const ico=getTipoIcon(tp,agendaTiposData);
            return (
              <span key={tp} style={{display:"flex",alignItems:"center",gap:4,fontWeight:700,whiteSpace:"nowrap",color:"var(--text)",flexShrink:0}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:c,display:"inline-block"}}/>
                {ico&&<Icon name={ico} size={12} color={c}/>}
                {totalByTipo[tp].toFixed(1)}h
              </span>
            );
          })}
          <span style={{marginLeft:"auto",color:"var(--accent)",fontWeight:800,fontSize:13,whiteSpace:"nowrap",flexShrink:0}}>{totalH.toFixed(1)}h</span>
          <Icon name={verDetallePie?"chevron-down":"chevron-up"} size={10} color="var(--text3)"/>
        </button>
      </div>

      {/* Footer */}
      <div className="cw-desk-only" style={{background:"var(--surface2)",borderTop:"1px solid var(--border)",padding:"6px 16px",display:"flex",gap:12,alignItems:"center",fontSize:11,flexShrink:0,flexWrap:"wrap"}}>
        {groupTipos.filter(tp=>totalByTipo[tp]>0).map(tp=>(
          <span key={tp} style={{display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:getTipoColor(tp,agendaTipos,agendaTiposData),display:"inline-block"}}/>
            <span style={{fontWeight:600,color:"var(--text2)"}}>{tp.replace("Tarea ","").replace("Formación","Form.")}:</span>
            <span style={{fontWeight:800,color:getTipoColor(tp,agendaTipos,agendaTiposData)}}>{filtered.filter(a=>a.type===tp).length}·{totalByTipo[tp].toFixed(1)}h</span>
          </span>
        ))}
        <span style={{marginLeft:"auto",color:"var(--accent)",fontWeight:800,fontSize:12}}>TOTAL: {totalH.toFixed(1)}h</span>
      </div>

      {/* Modal de tipo de entrada — crear, editar, archivar o reasignar */}
      {tipoModal&&(
        <TipoEntradaModal
          tipo={tipoModal.tipo}
          tiposData={agendaTiposData}
          usage={tipoModal.tipo?.name ? (tiposUsage[tipoModal.tipo.name]||{count:0,hours:0}) : null}
          canDelete={activeTipos.length>1||!!tipoModal.tipo?.archived}
          canArchive={activeTipos.length>1}
          canEditProd={isManagerRole}
          canVerIncidencias={canVerIncidencias}
          onSave={patch=>saveTipo(tipoModal.tipo,patch)}
          onDelete={opts=>deleteTipo(tipoModal.tipo,opts)}
          onClose={()=>setTipoModal(null)}/>
      )}
    </div>
  );
}
